import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Code,
  Divider,
  Grid,
  Group,
  Paper,
  Progress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconCashBanknoteMoveBack, IconCheck, IconDeviceFloppy, IconDownload, IconPlayerStop, IconRefresh, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";

import { api, ApiClientError } from "../api/client";
import { SimulationGateImpact, type DecisionCheckRow, type WindowTick } from "../api/schemas";
import { EmptyState, ErrorState, LoadingState } from "../components/ui";
import { GateImpactCard } from "../components/GateImpactCard";
import { KRONOS_FIELD_DESCRIPTIONS, KRONOS_MODEL_OPTIONS, kronosSignatureFromParams } from "../lib/kronosFields";
import { COUNTERPART_FIELD_DESCRIPTIONS } from "../lib/counterpartFields";
import { FAVORITE_HOLD_FIELD_DESCRIPTIONS, FAVORITE_HOLD_FIELD_LABELS } from "../lib/favoriteHoldFields";
import { candleTimeframeOptionsFor, timeframeLabel } from "../lib/candleTimeframes";
import { FIELD_COPY } from "../lib/featureCopy";
import {
  FeatureCardShell,
  FeatureColumnToolbar,
  FeatureGroupHeader,
  VALUE_FEATURE_DEFAULTS,
  isSectionActive,
  isSectionRemovable,
  useCollapsedPanels,
  useSortedSections,
  valueFieldFor,
  type FeatureSectionLike,
} from "../components/FeatureCards";
import { KronosCachePicker } from "../components/KronosCachePicker";
import { useMarket, useMarketId } from "../hooks/useMarket";
import { momentumSnapshot, resolveDecisionConfig, synthesizeDecisionChecks } from "./decisionChecksFallback";
import { exportSimulatedData, type ExportProgress } from "./manualSimulatorExport";
import { monoStyle } from "../theme";

interface SimCheckItem {
  name: string;
  passed: boolean | null;
  value?: unknown;
  threshold?: unknown;
  detail?: string | null;
}

interface SimTraceRow {
  check_ts: string;
  secs_left: number;
  outcome: "place" | "skip" | "blocked" | "monitor" | "already_placed" | "take_profit" | "stop_loss" | "momentum_sell" | "skip_after_wins";
  direction: "UP" | "DOWN" | null;
  favorite_odds: string | null;
  matching_candles: number | null;
  candles_evaluated: number | null;
  block_reason: string | null;
  checks: SimCheckItem[];
}

const FINAL_STATUSES = new Set(["succeeded", "failed", "cancelled"]);
const HIDDEN_FIELDS = new Set([
  "config_name",
  "display_timezone",
  "dry_run",
  "log_level",
  "log_file",
  "order_type",
  "state_file",
  "monitor_after_decision",
  "monitor_interval_seconds",
]);

const FEATURE_GROUPS: Array<{ title: string; description: string; enabledKey?: string; metaKey?: string; keys: string[] }> = [
  {
    title: "Config & Core Entry",
    description: "Which config version and date range to simulate, plus the trade itself: size, the odds band the favourite must sit in, when the first decision is evaluated, and the hard no-buy / no-sell cutoffs near close. Every other card below either filters this trade set (gates), extends it (overrides) or changes how it exits.",
    metaKey: "core",
    keys: [
      "trade_size_usdc",
      "min_odds",
      "max_odds",
      "decision_time_seconds",
      "no_buy_window_seconds",
      "no_sell_window_seconds",
    ],
  },
  {
    title: "Candle Filter",
    description: "Require the last N closed candles (of the chosen timeframe) to agree with the picked direction before entering. Turning this OFF bypasses the gate exactly like Min Matching Candles = 0; the candle counts are still computed for the trace.",
    enabledKey: "candle_filter_enabled",
    keys: [
      "candle_timeframe_seconds",
      "skip_first_candles",
      "min_matching_candles",
      "candles_to_check",
      "allow_live_candle",
      "live_candle_minute",
    ],
  },
  {
    title: "Entry Confirmation Delay",
    description: "Live + Sim: once every entry gate passes, keep re-checking for N consecutive seconds in the same direction before placing the order. Filters 1-tick head-fakes and reversals that start right after the signal. 0 = off.",
    metaKey: "entry_confirm",
    keys: ["entry_confirm_seconds"],
  },
  {
    title: "Simulated Order Placement Delay",
    description: "Sim-only: models the 1–5 s sign + POST latency between the signal and the exchange. The fill happens N seconds later at that tick's ask, and becomes a no-fill if the ask drifted above Max Odds or into the no-buy window. 0 = instant fill (optimistic).",
    metaKey: "sim_execution_delay",
    keys: ["sim_execution_delay_seconds"],
  },
  {
    title: "Multiple Orders per Window",
    description: "When ON the live bot may re-enter the same window after a position was bought and sold. The simulators always cap at one order per window, so leave this OFF for parity. Manual buys always bypass it.",
    enabledKey: "allow_multiple_orders_per_window",
    keys: [],
  },
  {
    title: "Win-streak Max Loss Threshold",
    description: "A resolved loss smaller than this many USDC does not break the win streak or reset compounding — it is treated as neutral, like a no-fill. 0 = any loss breaks the streak.",
    metaKey: "loss_streak_threshold",
    keys: ["loss_streak_continue_threshold_usdc"],
  },
  {
    title: "Late Buy",
    description: "Allow entry logic near close when candle checks are weaker.",
    enabledKey: "late_buy_enabled",
    keys: [
      "late_buy_within_seconds",
      "late_buy_trade_size_usdc",
    ],
  },
  {
    title: "Open-price Direction Gate",
    description: "Require the current price to be on the picked side of the window's open price (UP: current above open; DOWN: current below open). Uses the feed's own price series so it works for any market. Cold-start fails closed.",
    enabledKey: "open_price_gate_enabled",
    keys: [],
  },
  {
    title: "Open-price Candle Override",
    description: "When all other checks pass except the candle filter, still enter if the current price is on the correct side of the window open. Set within seconds to 0 for right away, or a positive value to only trigger in the last X seconds.",
    enabledKey: "open_price_override_enabled",
    keys: ["open_price_override_within_seconds"],
  },
  {
    title: "Open-price Cushion Gate",
    description: "Require the current price to be at least N basis points beyond the window open in the picked direction (UP: above open; DOWN: below open). Rejects at-the-money / wrong-side entries. Threshold is in bps of the open so BTC and ETH share one setting. Cold-start fails closed.",
    enabledKey: "open_price_cushion_enabled",
    keys: ["open_price_cushion_min_bps"],
  },
  {
    title: "Take Profit",
    description: "Exit early if implied probability reaches the target threshold.",
    enabledKey: "take_profit_enabled",
    keys: ["take_profit_threshold"],
  },
  {
    title: "Stop Loss",
    description: "Exit early when price action moves against the position.",
    enabledKey: "stop_loss_enabled",
    keys: [
      "stop_loss_threshold",
      "stop_loss_trigger_within_seconds",
      "stop_loss_btc_delta_enabled",
      "stop_loss_btc_delta_dollars",
      "stop_loss_btc_poll_interval_seconds",
      "stop_loss_btc_delta_mode",
      "stop_loss_btc_confirm_seconds",
      "stop_loss_last_candle_instant",
      "stop_loss_last_candle_seconds",
    ],
  },
  {
    title: "Underlying-Confirmed Protective Exits (Smart SL)",
    description: "Only let the odds Stop Loss and the Momentum Sell SL fire once the reference price has actually given back its cushion: the move from the window open toward the position must be <= N bps (0 = at/past the open). Prevents panic-selling a winning position when market makers briefly pull CLOB quotes. Take-profit exits are unaffected. Applies identically in live and simulation.",
    enabledKey: "exit_require_underlying_breach",
    keys: ["exit_underlying_breach_bps"],
  },
  {
    title: "Momentum Sell",
    description: "Exit early via a rolling average of post-fill ask prices. SL sub-mode sells when the average falls below fill ask by a threshold; TP sub-mode sells after the average first peaks above a profit threshold then falls back below it.",
    enabledKey: "momentum_sell_enabled",
    keys: [
      "momentum_sell_window_type",
      "momentum_sell_window_value",
      "momentum_sell_sl_enabled",
      "momentum_sell_sl_threshold_type",
      "momentum_sell_sl_threshold_value",
      "momentum_sell_sl_require_seconds",
      "momentum_sell_sl_start_at_seconds_left",
      "momentum_sell_tp_enabled",
      "momentum_sell_tp_threshold_type",
      "momentum_sell_tp_threshold_value",
      "momentum_sell_tp_min_seconds_below",
      "momentum_sell_tp_start_at_seconds_left",
      "momentum_sell_profit_is_win",
      "momentum_sell_loss_breaks_streak",
    ],
  },
  {
    title: "Pre-order Metrics Filter",
    description: "Rolling per-window averages + extremes of odds and BTC \u0394% observed prior to fill. Each of the 6 sub-filters can block placement when its value falls outside [min, max]. Avg-odds bands are separate per direction because UP/DOWN averages usually differ substantially.",
    enabledKey: "pre_order_metrics_enabled",
    keys: [
      "pre_order_up_avg_odds_filter_enabled",
      "pre_order_up_avg_odds_min",
      "pre_order_up_avg_odds_max",
      "pre_order_down_avg_odds_filter_enabled",
      "pre_order_down_avg_odds_min",
      "pre_order_down_avg_odds_max",
      "pre_order_up_min_odds_filter_enabled",
      "pre_order_up_min_odds_min",
      "pre_order_up_min_odds_max",
      "pre_order_up_min_pct_filter_enabled",
      "pre_order_up_min_pct_min",
      "pre_order_up_min_pct_max",
      "pre_order_down_min_odds_filter_enabled",
      "pre_order_down_min_odds_min",
      "pre_order_down_min_odds_max",
      "pre_order_down_max_pct_filter_enabled",
      "pre_order_down_max_pct_min",
      "pre_order_down_max_pct_max",
    ],
  },
  {
    title: "Skip After Wins",
    description: "Pause entries after a win streak and resume by configured rule.",
    enabledKey: "skip_after_wins_enabled",
    keys: [
      "skip_after_wins_threshold",
      "skip_after_wins_losses_to_resume",
    ],
  },
  {
    title: "Cascading Sizes",
    description: "Use staged position sizes instead of a fixed trade size.",
    enabledKey: "cascading_sizes_enabled",
    keys: ["cascading_sizes"],
  },
  {
    title: "Compounding Sizing",
    description: "Grow the order size during a win streak by a percent of each win's profit; reset to base after any loss.",
    enabledKey: "compounding_enabled",
    keys: ["compounding_win_pct", "compounding_max_size_usdc", "compounding_stop_after_wins", "compounding_stop_size_stay", "compounding_resume_from_saved"],
  },
  {
    title: "Trade Time Ranges",
    description: "Restrict simulated entries to configured local-time ranges.",
    enabledKey: "trade_time_ranges_enabled",
    keys: ["trade_time_ranges"],
  },
  {
    title: "Trade Days",
    description: "Restrict simulated entries to selected weekdays (in display timezone).",
    enabledKey: "trade_days_enabled",
    keys: ["trade_days_allowed"],
  },
  {
    title: "Momentum",
    description: "Require recent odds trend progression before allowing entry.",
    enabledKey: "odds_momentum_enabled",
    keys: [
      "odds_momentum_use_unique_odds",
      "odds_momentum_lookback",
      "odds_momentum_required_increasing",
      "odds_momentum_ignore_last_seconds",
      "odds_momentum_avg_lookback",
      "odds_momentum_avg_max_diff",
    ],
  },
  {
    title: "Pre-decision Average Odds",
    description: "Require the picked side's average mid odds from window open strictly before configured decision time to meet the minimum. The average freezes at decision time; later monitoring cannot change it. Blocks early entries and missing odds. No counterpart or Favorite Hold bypass.",
    enabledKey: "pre_decision_avg_odds_enabled",
    keys: ["pre_decision_avg_odds_min"],
  },
  {
    title: "Previous Windows Direction",
    description: "Require the last N resolved market windows (previous candles for this market) to have closed in the same direction as the picked side (e.g. 2 of the last 3 closed UP). Cold-start fails closed. Neutral / unresolved windows never match.",
    enabledKey: "prev_windows_check_enabled",
    keys: [
      "prev_windows_count",
      "prev_windows_min_matches",
    ],
  },
  {
    title: "Time in Min \u2194 Max Odds Range Before Decision Time",
    description: "Require the picked side's mid odds to have spent at least N seconds inside the configured min/max odds band before decision time (counted from window start on the 1Hz tick grid). Filters favourites that only just spiked into the band; applies to every entry path with no counterpart bypass.",
    enabledKey: "band_dwell_enabled",
    keys: [
      "band_dwell_min_seconds",
      "band_dwell_require_exceeds_opposite",
    ],
  },
  {
    title: "Max Favorite Flips",
    description: "Count how many times the picked side's mid odds dropped below the flip threshold since the warm-up skip (1Hz), and block the entry when that count exceeds the maximum. Hard gate (no counterpart / Favorite Hold bypass).",
    enabledKey: "flip_gate_enabled",
    keys: [
      "flip_gate_max_flips",
      "flip_gate_skip_seconds",
      "flip_gate_flip_threshold",
    ],
  },
  {
    title: "Peak Drawdown (Anti Falling-Knife)",
    description: "Block entries where the picked side is falling back INTO the odds band from above: the side's mid must not be more than X below its window peak, and the underlying must still retain at least R of its peak favourable move from the window open. Hard gate, no counterpart / Favorite Hold bypass; fails closed without data.",
    enabledKey: "peak_drawdown_gate_enabled",
    keys: ["max_drop_from_peak_odds", "min_price_excursion_retention"],
  },
  {
    title: "Volatility-Normalised Cushion (z-score)",
    description: "Require z = (current - open) / (realised 1s volatility x sqrt(secs_left)) >= Z in the picked direction. A fixed bps cushion is a near-lock on a quiet night and a coin-flip during a whipsaw; the z-score demands a larger cushion when the window is choppy or far from close. Hard gate; fails closed until >= 2 price changes are observed.",
    enabledKey: "vol_cushion_gate_enabled",
    keys: ["vol_cushion_min_z"],
  },
  {
    title: "Underlying Velocity (last K seconds)",
    description: "Require the reference price to have moved toward the picked direction by at least N bps over the last K seconds (0 bps = merely not moving against you). Catches CLOB odds lagging a fast spot reversal right before entry. Hard gate; fails closed when no tick >= K seconds old exists yet.",
    enabledKey: "underlying_velocity_gate_enabled",
    keys: ["underlying_velocity_lookback_seconds", "underlying_velocity_min_bps"],
  },
  {
    title: "Open-Side Streak / Open Crossings",
    description: "Require the reference price to have stayed continuously on the picked side of the window open for the last N seconds, and/or block windows where it crossed the open more than K times (pinning coin-flips). 0 disables each sub-check. Hard gate.",
    enabledKey: "open_side_gate_enabled",
    keys: ["open_side_streak_seconds", "max_open_crosses"],
  },
  {
    title: "Bid-Ask Spread Cap",
    description: "Require (ask - bid) on the picked side to be <= the cap at entry. A wide spread means you pay far above mid and market makers are pricing jump risk. Hard gate.",
    enabledKey: "max_spread_gate_enabled",
    keys: ["max_spread"],
  },
  {
    title: "Counterpart Confirmation Gate",
    description: "Filter (not override): block the entry when the time-aligned counterpart market (BTC \u2194 ETH, same timeframe) disagrees. 'price' mode blocks when the counterpart's underlying has moved AGAINST the picked direction by more than N bps from its open; 'favorite' mode blocks when the counterpart's favourite side is the opposite direction. Fails closed when the counterpart window is missing.",
    enabledKey: "counterpart_confirm_gate_enabled",
    keys: ["counterpart_confirm_mode", "counterpart_confirm_max_opposing_bps"],
  },
  {
    title: "Kronos Forecast",
    description: "Gate entries on the Kronos foundation-model window forecast: require the predicted close direction to match the picked side and the predicted probability to clear a confidence floor. Reads the precomputed kronos_predictions cache (kronos-service + kronos_precompute); uncached windows fail closed. Pick which model to read (must already be precomputed for this market in Settings → Kronos). The other inference params (lookback/timeframe/sampling) form the rest of the cache signature and live in Config — changing them requires a full precompute, so they are set there, not per-sim.",
    enabledKey: "kronos_check_enabled",
    keys: [
      "kronos_require_direction",
      "kronos_min_confidence",
      "kronos_min_edge_over_ask",
      "kronos_model",
    ],
  },
  {
    title: "Counterpart Market Override",
    description: COUNTERPART_FIELD_DESCRIPTIONS.counterpart_override_enabled!,
    enabledKey: "counterpart_override_enabled",
    keys: [
      "counterpart_odds_threshold",
      "counterpart_decide_within_pct",
      "counterpart_stable_seconds",
      "counterpart_follow_direction",
      "counterpart_follow_exclusive",
      "counterpart_require_trend",
      "counterpart_bypass_candle",
      "counterpart_bypass_open_price",
      "counterpart_bypass_prev_windows",
      "counterpart_bypass_kronos",
      "counterpart_bypass_momentum",
      "counterpart_bypass_pre_order",
      "counterpart_bypass_time_ranges",
      "counterpart_bypass_min_odds",
      "counterpart_bypass_max_odds",
    ],
  },
  {
    title: "Counterpart Matching Order",
    description: COUNTERPART_FIELD_DESCRIPTIONS.counterpart_match_order_enabled!,
    enabledKey: "counterpart_match_order_enabled",
    keys: [
      "counterpart_match_order_require_trend",
      "counterpart_match_order_require_candle",
      "counterpart_match_order_require_open_price",
      "counterpart_match_order_require_prev_windows",
      "counterpart_match_order_require_kronos",
      "counterpart_match_order_require_momentum",
      "counterpart_match_order_require_pre_order",
      "counterpart_match_order_require_time_ranges",
      "counterpart_match_order_require_min_odds",
      "counterpart_match_order_require_max_odds",
    ],
  },
  {
    title: "Favorite Hold (3-Tier)",
    description: FAVORITE_HOLD_FIELD_DESCRIPTIONS.favorite_hold_enabled!,
    enabledKey: "favorite_hold_enabled",
    keys: [
      "favorite_hold_flip_threshold",
      "favorite_hold_skip_seconds",
      "favorite_hold_tier1_enabled",
      "favorite_hold_tier1_min_odds",
      "favorite_hold_tier1_dwell_seconds",
      "favorite_hold_tier2_enabled",
      "favorite_hold_tier2_min_rise",
      "favorite_hold_tier2_check_seconds",
      "favorite_hold_tier2_require_candle",
      "favorite_hold_tier2_require_open_price",
      "favorite_hold_tier2_require_prev_windows",
      "favorite_hold_tier2_require_kronos",
      "favorite_hold_tier2_require_momentum",
      "favorite_hold_tier2_require_pre_order",
      "favorite_hold_tier2_require_time_ranges",
      "favorite_hold_tier2_require_min_odds",
      "favorite_hold_tier3_enabled",
      "favorite_hold_tier3_bypass_candle",
      "favorite_hold_tier3_bypass_open_price",
      "favorite_hold_tier3_bypass_prev_windows",
      "favorite_hold_tier3_bypass_kronos",
      "favorite_hold_tier3_bypass_momentum",
      "favorite_hold_tier3_bypass_pre_order",
      "favorite_hold_tier3_bypass_time_ranges",
      "favorite_hold_tier3_bypass_min_odds",
    ],
  },
  {
    title: "Hedge at Entry",
    description: "When an order is placed, also buy the OPPOSITE side at the same tick with the configured % of the main trade size. The hedge always rides to window resolution (it is never sold by take-profit / stop-loss / momentum-sell exits, which only close the main leg), capping the loss when the market flips against the entry at the cost of part of the win-side profit. Pure P&L overlay: win/loss counts, streaks, compounding and cascading sizes still follow the main leg only.",
    enabledKey: "hedge_enabled",
    keys: [
      "hedge_pct",
    ],
  },
];

interface SimOutcomeRow {
  slug: string;
  result: "win" | "loss" | "skip" | "no_bet" | "blocked" | "take_profit" | "stop_loss" | "momentum_sell" | "unresolved";
  historical_no_fill: boolean;
  direction: "UP" | "DOWN" | null;
  winning_direction: "UP" | "DOWN" | null;
  favorite_odds: string | null;
  pnl_usdc: number;
  trade_size_usdc: number | null;
  win_streak_at_bet: number;
  decision_outcome: string;
  counterpart_override_active: boolean;
  counterpart_match_order_active: boolean;
  check_trace: SimTraceRow[];
}

/**
 * True when this order was placed BECAUSE the counterpart-market override
 * fired (i.e. the override was active on the tick that placed the order).
 * The backend's persisted outcome flag survives stripped check traces.
 * Earlier passing checks and matching-order entries must not be attributed
 * to the counterpart override.
 */
function orderUsedCounterpartOverride(row: SimOutcomeRow): boolean {
  return row.direction != null && row.counterpart_override_active && !row.counterpart_match_order_active;
}

/**
 * True when this order was placed BECAUSE the counterpart matching-order
 * feature fired (the counterpart market held an active order on the placing
 * tick). The backend sets ``counterpart_match_order_active`` directly on the
 * outcome (survives the check_trace strip); fall back to the trace when present.
 */
function orderUsedCounterpartMatchOrder(row: SimOutcomeRow): boolean {
  if (row.counterpart_match_order_active) return true;
  const placeTrace = row.check_trace.find((t) => t.outcome === "place");
  const traces = placeTrace ? [placeTrace] : row.check_trace;
  return traces.some((t) =>
    (t.checks ?? []).some((c) => c.name === "counterpart_match_order" && c.passed === true),
  );
}

interface SimSummaryView {
  total_windows: number;
  windows_with_bet: number;
  wins: number;
  losses: number;
  take_profits: number;
  stop_losses: number;
  momentum_sells: number;
  momentum_sell_profit_count: number;
  momentum_sell_loss_count: number;
  momentum_sell_profit_pnl_usdc: string;
  momentum_sell_loss_usdc: string;
  momentum_sell_total_pnl_usdc: string;
  losses_including_stop_losses: number;
  no_fill_wins: number;
  no_fill_losses: number;
  skips: number;
  blocked: number;
  no_bet: number;
  unresolved: number;
  pnl_usdc: string;
  total_won_usdc: string;
  take_profit_total_won_usdc: string;
  total_lost_usdc: string;
  stop_loss_total_lost_usdc: string;
  fees_usdc: string;
  win_rate: string;
  average_odds: string | null;
  time_range_breakdown: SimRangeBreakdownRow[];
  day_breakdown: SimDayBreakdownRow[];
  gate_impact: SimulationGateImpact | null;
  outcomes: SimOutcomeRow[];
}

/** Feature key (enabledKey / metaKey) -> card title, for the Gate Impact table. */
const FEATURE_TITLES: Record<string, string> = Object.fromEntries(
  FEATURE_GROUPS.flatMap((g) => {
    const key = g.enabledKey ?? g.metaKey;
    return key ? [[key, g.title]] : [];
  }),
);

interface SimRangeBreakdownRow {
  range: string;
  total_windows: number;
  windows_with_bet: number;
  wins: number;
  losses: number;
  win_rate: string;
  total_won_usdc: string;
  total_lost_usdc: string;
  net_pnl_usdc: string;
}

interface SimDayBreakdownRow {
  day: string;
  total_windows: number;
  windows_with_bet: number;
  wins: number;
  losses: number;
  win_rate: string;
  total_won_usdc: string;
  total_lost_usdc: string;
  net_pnl_usdc: string;
}

// Canonical weekday ordering (Mon=0..Sun=6) so the day breakdown table
// renders in week order regardless of how ``trade_days_allowed`` is typed.
const DAY_LABEL_ORDER: Record<string, number> = {
  MON: 0, MONDAY: 0,
  TUE: 1, TUES: 1, TUESDAY: 1,
  WED: 2, WEDS: 2, WEDNESDAY: 2,
  THU: 3, THUR: 3, THURS: 3, THURSDAY: 3,
  FRI: 4, FRIDAY: 4,
  SAT: 5, SATURDAY: 5,
  SUN: 6, SUNDAY: 6,
};

function dayLabelOrder(label: string): number {
  const idx = DAY_LABEL_ORDER[label.trim().toUpperCase()];
  return idx === undefined ? 99 : idx;
}

type OrderWindowListItem =
  | { type: "date"; key: string; label: string }
  | { type: "row"; key: string; row: SimOutcomeRow; title: string };

const EDITABLE_KEYS = new Set(
  FEATURE_GROUPS.flatMap((group) => [
    ...(group.enabledKey ? [group.enabledKey] : []),
    ...group.keys,
  ]),
);
const SIMULATION_ONLY_KEYS = ["trade_time_ranges_split_results", "trade_days_split_results"] as const;
// Earliest `market_windows.window_start` currently in prod DB:
// 2026-05-15T18:00:00Z (UTC) == 2026-05-15T14:00 (EDT, datetime-local input)
const DEFAULT_SIM_FROM = "2026-05-15T14:00";
const MAX_TRADE_TIME_RANGES = 300;

function countTradeTimeRanges(raw: string): number {
  return raw
    .split(",")
    .map((piece) => piece.trim())
    .filter((piece) => piece.length > 0)
    .length;
}

function assertTradeTimeRangesMax(raw: string): void {
  const count = countTradeTimeRanges(raw);
  if (count > MAX_TRADE_TIME_RANGES) {
    throw new Error(
      `trade_time_ranges supports at most ${MAX_TRADE_TIME_RANGES} ranges (received ${count})`,
    );
  }
}

function asDirection(value: unknown): "UP" | "DOWN" | null {
  return value === "UP" || value === "DOWN" ? value : null;
}

function toIsoOrUndef(value: string): string | undefined {
  if (!value) return undefined;
  const dt = new Date(value);
  return Number.isFinite(dt.getTime()) ? dt.toISOString() : undefined;
}

function statusColor(status: string | null | undefined): string {
  switch (status) {
    case "queued": return "yellow";
    case "running": return "blue";
    case "succeeded": return "green";
    case "failed": return "red";
    case "cancelled": return "gray";
    default: return "gray";
  }
}

function toFiniteNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function directionalMidOdds(tick: WindowTick, direction: "UP" | "DOWN"): number | null {
  const bid = direction === "UP" ? tick.up_bid : tick.down_bid;
  const ask = direction === "UP" ? tick.up_ask : tick.down_ask;
  if (bid == null || ask == null) return null;
  const b = Number(bid);
  const a = Number(ask);
  if (!Number.isFinite(b) || !Number.isFinite(a)) return null;
  return (b + a) / 2;
}

// V3 multi-market: returns the per-second offset of ``ts`` from
// ``windowStart``, clamped to ``[0, windowSeconds - 1]``. The hard 299
// upper bound in V2 was a 5-minute assumption that silently corrupted
// 15m/1h/4h windows (every tick past minute 5 got bucketed into the
// last second of M1), causing the Price feed table to render only
// dashes for any slice past the first 5 minutes.
function checkSecondOffset(
  ts: string,
  windowStart: string,
  windowSeconds: number = 300,
): number | null {
  const tsMs = Date.parse(ts);
  const startMs = Date.parse(windowStart);
  if (!Number.isFinite(tsMs) || !Number.isFinite(startMs)) return null;
  const sec = Math.floor((tsMs - startMs) / 1000);
  if (!Number.isFinite(sec)) return null;
  const maxOffset = Math.max(0, Math.floor(windowSeconds) - 1);
  return Math.max(0, Math.min(maxOffset, sec));
}

function fmtPct1(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function fmtUsd(v: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function findPlaceCheck(checks: SimTraceRow[], direction: "UP" | "DOWN" | null): SimTraceRow | null {
  for (const row of checks) {
    if (row.outcome !== "place") continue;
    if (!direction || row.direction === direction) return row;
  }
  return null;
}

function collapseChecksBySecond(checks: SimTraceRow[], windowStart: string): SimTraceRow[] {
  if (checks.length === 0) return checks;
  // Derive a reference point: prefer the explicit windowStart, fall back to
  // the earliest check_ts in the array so this function never no-ops and
  // leaves duplicate-second rows uncollapsed (which was the source of the
  // "ORDER PLACED" badge appearing on a monitor/already_placed row).
  const startMs = windowStart ? Date.parse(windowStart) : Date.parse(checks[0]!.check_ts);
  if (!Number.isFinite(startMs)) return checks;
  const bySecond = new Map<number, SimTraceRow>();
  for (const row of checks) {
    const rowMs = Date.parse(row.check_ts);
    if (!Number.isFinite(rowMs)) continue;
    const sec = Math.floor((rowMs - startMs) / 1000);
    const existing = bySecond.get(sec);
    // Always prefer the "place" row over any other outcome at the same second.
    // A monitor/already_placed row carrying the same tick timestamp must never
    // overwrite the row that holds the actual bet-placement conditions.
    if (existing && existing.outcome === "place" && row.outcome !== "place") continue;
    bySecond.set(sec, row);
  }
  return Array.from(bySecond.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, row]) => row);
}

function simulatedCheckStatus(
  row: SimTraceRow,
  placeTs: string | null,
  exitTs: string | null,
  result: SimOutcomeRow["result"],
): { label: string; tone: "green" | "red" | "yellow" | "gray" } {
  if (placeTs && row.check_ts === placeTs) return { label: "ORDER PLACED", tone: "green" };
  if (exitTs && row.check_ts === exitTs && result === "take_profit") return { label: "TAKE PROFIT HIT", tone: "yellow" };
  if (exitTs && row.check_ts === exitTs && result === "stop_loss") return { label: "STOP LOSS HIT", tone: "yellow" };
  if (exitTs && row.check_ts === exitTs && result === "momentum_sell") return { label: "MOMENTUM SELL HIT", tone: "yellow" };
  if (row.outcome === "blocked") return { label: "CHECKS NOT PASSED", tone: "gray" };
  if (row.outcome === "monitor") return { label: "MONITORING", tone: "gray" };
  if (row.outcome === "skip") return { label: "SKIP", tone: "yellow" };
  if (row.outcome === "already_placed") return { label: "ALREADY PLACED", tone: "green" };
  return { label: "CHECKS PASSED", tone: "green" };
}

function checkByName(row: SimTraceRow, name: string): SimCheckItem | null {
  for (const c of row.checks) {
    if (c.name === name) return c;
  }
  return null;
}

function formatPctNum(value: unknown): string {
  const n = typeof value === "string" ? Number(value) : typeof value === "number" ? value : NaN;
  return Number.isFinite(n) ? `${Math.round((n as number) * 100)}%` : "—";
}

function passFailIcon(passed: boolean | null): JSX.Element {
  if (passed == null) return <Text c="dimmed" size="md">—</Text>;
  if (passed) return <IconCheck size={20} color="var(--mantine-color-green-6)" />;
  return <IconX size={20} color="var(--mantine-color-red-6)" />;
}

function pillStylesForTone(tone: "green" | "red" | "yellow" | "gray" | string): React.CSSProperties {
  const t = String(tone || "gray").toLowerCase();
  let bg = "var(--mantine-color-gray-8)";
  if (t === "green") bg = "var(--mantine-color-green-8)";
  else if (t === "red") bg = "var(--mantine-color-red-8)";
  else if (t === "yellow") bg = "var(--mantine-color-yellow-9)";
  return {
    backgroundColor: bg,
    color: "var(--mantine-color-white)",
    fontWeight: 800,
    fontSize: "0.92rem",
    lineHeight: 1.2,
    padding: "7px 12px",
    minWidth: 56,
    textAlign: "center",
    letterSpacing: 0.3,
    textShadow: "0 1px 1px rgba(0,0,0,0.35)",
  };
}

function momentumPassed(row: SimTraceRow): boolean | null {
  const m = checkByName(row, "odds_momentum");
  return m ? m.passed : null;
}

function momentumLabel(row: SimTraceRow, priorRows: SimTraceRow[]): string {
  const own = checkByName(row, "odds_momentum");
  // "place" rows are returned as-is from enrichSimChecksWithMomentum. If they
  // have no stored odds_momentum check, momentum was disabled in that
  // simulation run. Don't fall back to a priorRow's synthesised value (which
  // is computed from the current form config and would show a misleading
  // "21/22" label next to ORDER PLACED).
  if (!own && row.outcome === "place") return "—";
  const f = own ?? (() => {
    for (let i = priorRows.length - 1; i >= 0; i -= 1) {
      const prev = checkByName(priorRows[i]!, "odds_momentum");
      if (prev) return prev;
    }
    return null;
  })();
  if (!f) return "—";
  if (typeof f.value === "string") {
    const m = f.value.match(/(\d+\s*\/\s*\d+)/);
    if (m?.[1]) return m[1].replace(/\s+/g, "");
  }
  if (f.value && typeof f.value === "object") {
    const v = f.value as Record<string, unknown>;
    const increasing = v.increasing;
    const required = (f.threshold as Record<string, unknown> | undefined)?.required_increasing;
    if (typeof increasing === "number" || typeof increasing === "string") {
      if (typeof required === "number" || typeof required === "string") {
        return `${String(increasing)}/${String(required)}`;
      }
    }
  }
  return "—";
}

function avgMomentumPlaceholder(): JSX.Element {
  return (
    <Stack gap={2} style={{ whiteSpace: "nowrap" }}>
      <Text style={monoStyle} fw={600} c="dimmed">—</Text>
      <Text style={monoStyle} size="sm" c="dimmed">—</Text>
    </Stack>
  );
}

function avgMomentumCell(row: SimTraceRow): JSX.Element {
  const entry = checkByName(row, "odds_momentum");
  if (!entry) return avgMomentumPlaceholder();
  const value = entry.value && typeof entry.value === "object" && !Array.isArray(entry.value)
    ? (entry.value as Record<string, unknown>)
    : null;
  const threshold = entry.threshold && typeof entry.threshold === "object" && !Array.isArray(entry.threshold)
    ? (entry.threshold as Record<string, unknown>)
    : null;
  if (!value || !threshold) return avgMomentumPlaceholder();
  const toNum = (v: unknown): number | null => {
    const n = typeof v === "string" ? Number(v) : typeof v === "number" ? v : NaN;
    return Number.isFinite(n) ? n : null;
  };
  const avgLookback = toNum(threshold.avg_lookback);
  const avgMaxDiff = toNum(threshold.avg_max_diff);
  if (avgLookback == null || avgLookback <= 0 || avgMaxDiff == null || avgMaxDiff <= 0) {
    return avgMomentumPlaceholder();
  }
  const avgDiff = toNum(value.avg_diff);
  const avgPassed = typeof value.avg_passed === "boolean" ? value.avg_passed : null;
  const avgPriorRaw = Array.isArray(value.avg_prior) ? (value.avg_prior as unknown[]) : [];
  const avgPrior = avgPriorRaw.map((v) => toNum(v)).filter((n): n is number => n != null);
  const current = toNum(value.current) ?? toNum(row.favorite_odds);
  let avgVal: number | null = null;
  if (avgPrior.length > 0) {
    avgVal = avgPrior.reduce((s, v) => s + v, 0) / avgPrior.length;
  } else if (current != null && avgDiff != null) {
    avgVal = current - avgDiff;
  }
  if (avgVal == null || avgDiff == null) return avgMomentumPlaceholder();
  const diffLabel = `${avgDiff >= 0 ? "+" : "-"}${Math.abs(avgDiff).toFixed(3)}`;
  const diffColor = avgPassed === false
    ? "var(--mantine-color-red-7)"
    : avgPassed === true ? "var(--mantine-color-green-7)" : undefined;
  const sampleCount = avgPrior.length > 0 ? avgPrior.length : avgLookback;
  return (
    <Stack gap={2} style={{ whiteSpace: "nowrap" }}>
      <Group gap={6} wrap="nowrap" align="baseline">
        <Text style={monoStyle} fw={600}>{avgVal.toFixed(3)}</Text>
        <Text style={monoStyle} size="xs" c="dimmed">n={sampleCount}</Text>
      </Group>
      <Group gap={4} wrap="nowrap">
        <Text style={{ ...monoStyle, color: diffColor }} size="sm">{diffLabel}</Text>
        <Text style={monoStyle} size="sm" c="dimmed">/ {avgMaxDiff.toFixed(3)}</Text>
        {passFailIcon(avgPassed)}
      </Group>
    </Stack>
  );
}

function candlesLabel(row: SimTraceRow, priorRows: SimTraceRow[]): string {
  if (row.matching_candles != null && row.candles_evaluated != null) {
    return `${row.matching_candles} / ${row.candles_evaluated}`;
  }
  for (let i = priorRows.length - 1; i >= 0; i -= 1) {
    const prev = priorRows[i]!;
    if (prev.matching_candles != null && prev.candles_evaluated != null) {
      return `${prev.matching_candles} / ${prev.candles_evaluated}`;
    }
  }
  return "— / —";
}

/**
 * Format a numeric odds value as a 3-decimal string matching the chip pill
 * format ("0.815"). Returns null for non-finite inputs so callers can skip.
 */
function fmtOdds(n: unknown): string | null {
  const v = typeof n === "number" ? n : typeof n === "string" ? Number(n) : NaN;
  if (!Number.isFinite(v)) return null;
  return v.toFixed(3);
}

/**
 * Pull odds from the engine's structured `odds_momentum.value` shape:
 *   { observed, increasing, current: number, prior: number[] }
 *
 * The engine emits this for every check_trace row that evaluated momentum,
 * including `place` rows. The values are guaranteed to match exactly what
 * the engine compared against the threshold, on the correct favorite
 * direction, with the configured lookback. Returns null when the row's
 * odds_momentum check is missing or doesn't have this structured shape
 * (e.g. legacy/synthesised rows that only expose a `ratio` string value).
 */
function engineMomentumOdds(row: SimTraceRow): string[] | null {
  const f = checkByName(row, "odds_momentum");
  if (!f) return null;
  const v = f.value;
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const obj = v as Record<string, unknown>;
  const priorRaw = obj.prior;
  const currentRaw = obj.current;
  if (!Array.isArray(priorRaw)) return null;
  const prior = priorRaw
    .map((x) => fmtOdds(x))
    .filter((s): s is string => s !== null);
  const current = fmtOdds(currentRaw);
  if (current == null) return prior;
  return [...prior, current];
}

function uniqueOddsValues(row: SimTraceRow, priorRows: SimTraceRow[]): string[] {
  // Engine-authoritative odds for THIS row's momentum evaluation. This is
  // the only source that's guaranteed to match the engine's actual check
  // (correct direction, correct lookback window). Always prefer it.
  const fromEngine = engineMomentumOdds(row);
  if (fromEngine && fromEngine.length > 0) return fromEngine;

  const f = checkByName(row, "unique_odds_checked");
  if (f) {
    if (Array.isArray(f.value)) {
      return f.value.filter((x): x is string => typeof x === "string");
    }
    if (typeof f.value === "string") {
      return f.value.split(",").map((v) => v.trim()).filter(Boolean);
    }
  }

  for (let i = priorRows.length - 1; i >= 0; i -= 1) {
    const prev = priorRows[i]!;
    // Try the engine-authoritative shape on prior rows before falling
    // back to the legacy `unique_odds_checked` field.
    const prevEngine = engineMomentumOdds(prev);
    if (prevEngine && prevEngine.length > 0) return prevEngine;
    const prevCheck = checkByName(prev, "unique_odds_checked");
    if (!prevCheck) continue;
    if (Array.isArray(prevCheck.value)) {
      const values = prevCheck.value.filter((x): x is string => typeof x === "string");
      if (values.length > 0) return values;
      continue;
    }
    if (typeof prevCheck.value === "string") {
      const values = prevCheck.value.split(",").map((v) => v.trim()).filter(Boolean);
      if (values.length > 0) return values;
    }
  }

  return [];
}

function checkedOddsSummary(row: SimTraceRow, priorRows: SimTraceRow[]): { passed: boolean | null; values: string[] } {
  const values = uniqueOddsValues(row, priorRows);
  let passed = momentumPassed(row);
  if (passed == null && (row.outcome === "place" || row.outcome === "already_placed")) {
    passed = true;
  }
  return { passed, values };
}

function oddsChips(values: string[]): Array<{ value: string; passed: boolean | null }> {
  if (values.length === 0) return [];
  const nums = values.map((v) => Number(v));
  if (nums.some((n) => !Number.isFinite(n))) {
    return values.map((value) => ({ value, passed: null }));
  }
  const current = nums[nums.length - 1]!;
  return values.map((value, i) => {
    if (i === values.length - 1) return { value, passed: true };
    return { value, passed: nums[i]! < current };
  });
}

function asSimTraceRow(row: DecisionCheckRow): SimTraceRow {
  const rawChecks = Array.isArray(row.checks) ? row.checks : [];
  const checks: SimCheckItem[] = rawChecks
    .filter((x): x is Record<string, unknown> => Boolean(x && typeof x === "object"))
    .map((x) => ({
      name: String(x.name ?? "unknown"),
      passed: typeof x.passed === "boolean" ? x.passed : x.passed == null ? null : Boolean(x.passed),
      value: x.value,
      threshold: x.threshold,
      detail: x.detail == null ? null : String(x.detail),
    }));
  return {
    check_ts: row.check_ts,
    secs_left: 0,
    outcome: row.outcome,
    direction: row.direction,
    favorite_odds: row.favorite_odds,
    matching_candles: row.matching_candles,
    candles_evaluated: row.candles_evaluated,
    block_reason: row.block_reason,
    checks,
  };
}

function enrichSimChecksWithMomentum(
  rows: SimTraceRow[],
  ticks: WindowTick[],
  cfg: ReturnType<typeof resolveDecisionConfig>,
): SimTraceRow[] {
  if (rows.length === 0 || ticks.length === 0) return rows;
  const sortedTicks = [...ticks].sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts));
  return rows.map((row) => {
    if (!row.direction) return row;
    // "place" rows must NEVER be re-synthesised. The engine recorded the exact
    // conditions it evaluated. If odds_momentum_enabled was false in the
    // simulation, no odds_momentum check was stored — synthesising one from
    // the current form config would inject a failing check (e.g. 21/22 ✗)
    // that never actually gated the bet. Always trust the persisted trace.
    if (row.outcome === "place") {
      return row;
    }
    const cutoff = Date.parse(row.check_ts);
    if (!Number.isFinite(cutoff)) return row;
    const upTo = sortedTicks.filter((t) => Date.parse(t.ts) <= cutoff);
    if (upTo.length === 0) return row;
    const lastTick = upTo[upTo.length - 1]!;
    const secsLeft = Number.isFinite(row.secs_left) ? row.secs_left : lastTick.secs_left;
    const snap = momentumSnapshot(upTo, row.direction, secsLeft, cfg);

    const existing = row.checks.filter(
      (c) => c.name !== "odds_momentum" && c.name !== "unique_odds_checked",
    );

    return {
      ...row,
      checks: [
        ...existing,
        {
          name: "odds_momentum",
          passed: snap.passed,
          value: snap.ratio,
          threshold: `${cfg.odds_momentum_required_increasing}/${cfg.odds_momentum_lookback}`,
          detail: snap.detail,
        },
        {
          name: "unique_odds_checked",
          passed: null,
          value: snap.checkedOdds,
          threshold: cfg.odds_momentum_lookback + 1,
          detail: `${snap.checkedOdds.length}/${cfg.odds_momentum_lookback + 1} recent ${cfg.odds_momentum_use_unique_odds ? "unique" : "recorded"} odds`,
        },
      ],
    };
  });
}

function resultColor(result: string): string {
  if (result === "win") return "green";
  if (result === "loss") return "red";
  if (result === "take_profit") return "teal";
  if (result === "stop_loss") return "orange";
  if (result === "momentum_sell") return "orange";
  if (result === "skip") return "yellow";
  return "gray";
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) return `${error.code}: ${error.message}`;
  if (error instanceof Error) return error.message;
  return "unknown error";
}

// Per-field descriptions shown below the label in simulator settings panels.
const FIELD_DESCRIPTIONS: Record<string, string> = {
  momentum_sell_window_type: "Fixed seconds: always use this many seconds of ask history. Percentage: use this % of secs_left at fill time.",
  momentum_sell_window_value: "Window size in seconds (number mode) or as % of secs_left at fill (percentage mode). E.g. 10 seconds or 50% of remaining time.",
  momentum_sell_sl_enabled: "When on, fire a sell when the rolling average falls below fill ask by the SL threshold for the required seconds.",
  momentum_sell_sl_threshold_type: "Odds points: avg drops by this many points below fill ask. Percentage: avg drops by this % of fill ask.",
  momentum_sell_sl_threshold_value: "SL threshold value. Enter a plain number — no % sign. E.g. 0.05 for odds-points mode, or 50 for 50% in percentage mode.",
  momentum_sell_sl_require_seconds: "Consecutive seconds the average must stay below the SL threshold before selling. 0 = sell immediately on first qualifying tick.",
  momentum_sell_sl_start_at_seconds_left: "Delay SL evaluation until this many seconds are left in the window. 0 = start as soon as the buffer is full (default). E.g. 15 = SL checks only begin when ≤ 15 seconds remain.",
  momentum_sell_tp_enabled: "When on, activate TP mode: once avg reaches fill_ask + threshold, sell when avg falls back below that level.",
  momentum_sell_tp_threshold_type: "Odds points: avg must rise this many points above fill ask to activate TP. Percentage: avg must rise by this % of fill ask.",
  momentum_sell_tp_threshold_value: "TP threshold value. Enter a plain number — no % sign. E.g. 0.05 for odds-points mode, or 5 for 5% in percentage mode.",
  momentum_sell_tp_min_seconds_below: "After TP activates, seconds the average must stay below the TP threshold before selling. 0 = sell on first tick below threshold.",
  momentum_sell_tp_start_at_seconds_left: "Delay TP evaluation until this many seconds are left in the window. 0 = start as soon as the buffer is full (default). E.g. 15 = TP checks only begin when ≤ 15 seconds remain.",
  momentum_sell_profit_is_win: "When on, a momentum sell that closes in profit counts as a win: it extends the win streak and continues compounding, just like a take-profit exit.",
  momentum_sell_loss_breaks_streak: "When on (default), a momentum sell that closes at a loss breaks the win streak and resets compounding state. Turn off to treat loss-side momentum sells as neutral — the streak and compounding sequence are preserved — so a defensive SL exit doesn't penalise the active cascade.",
  loss_streak_continue_threshold_usdc: "Max loss amount (USDC) the bot is allowed to take while still continuing the active win streak. Any resolved loss with |P/L| at or above this amount resets the streak; losses strictly below it are treated as neutral. 0 disables — every loss resets the streak. Note: Momentum Sell SL has its own setting (Loss-side sell breaks win streak) that controls whether momentum-sell losses break the streak.",
  allow_multiple_orders_per_window: "When off (default), at most one order is placed per window (matching the simulation engines; manual buys always bypass this gate). When on, the trading bot may enter a new position in the same window after a previous order has been bought and sold.",
  entry_confirm_seconds: "Live + Sim: consecutive seconds all entry gates must remain passing in the same direction before placing the order. 0 = place immediately on first passing tick.",
  sim_execution_delay_seconds: "Sim-only: models the 1–5s live order execution latency after all checks pass. The simulated fill occurs N seconds later at that tick's ask price (and fails as a no-fill if the ask drifted above max_odds or past no_buy_window_seconds). 0 = instant fill.",
  max_drop_from_peak_odds: "Block when the picked side's mid is more than this many odds points (0.0 – 1.0) below the highest mid it reached earlier in this window. E.g. 0.06 rejects buying at 0.85 after a 0.94 peak. 0 disables this sub-check.",
  min_price_excursion_retention: "Require (current move from open) / (peak favourable move from open) >= this fraction. 0.55 means the underlying still holds at least 55% of its best excursion. 0 disables this sub-check.",
  vol_cushion_min_z: "Minimum z-score. z = signed (current - open) / (stdev of 1-second price changes so far x sqrt(secs_left)). Typical 1.5–2.5.",
  underlying_velocity_lookback_seconds: "Look back this many seconds for the reference price (uses the newest tick at least K seconds old).",
  underlying_velocity_min_bps: "Minimum signed move over the lookback, in bps toward the picked direction. 0 = must not be moving against the pick; negative values tolerate a small adverse drift.",
  open_side_streak_seconds: "Consecutive 1Hz samples (seconds) the reference price must have stayed strictly on the picked side of the window open, ending at the current tick. 0 disables.",
  max_open_crosses: "Maximum number of times the reference price may have crossed the window open so far this window. 0 disables (set 1 to allow at most one cross).",
  max_spread: "Maximum (ask - bid) on the picked side at the entry tick, in odds (e.g. 0.02 = 2 cents).",
  counterpart_confirm_mode: "'price': compare the counterpart's underlying move from its open (blocks if opposing by more than the bps cap). 'favorite': require the counterpart's favourite side (higher mid) to match the picked direction.",
  counterpart_confirm_max_opposing_bps: "'price' mode only: largest counterpart move AGAINST the picked direction (bps from the counterpart's open) that is still tolerated. 0 = any opposing move blocks.",
  kronos_min_edge_over_ask: "Require Kronos P(picked side) >= rounded entry ask + this edge (e.g. 0.03). Paying 0.87 on a 0.79 forecast is negative EV. 0 disables (the plain confidence floor still applies).",
  exit_underlying_breach_bps: "Protective exits (odds SL / Momentum Sell SL) are allowed only when the reference price's signed move from the window open toward the position is <= this many bps. 0 = at or past the open; positive values allow exits while a small cushion remains.",
  candle_timeframe_seconds: "Width of one candle (seconds). 0 falls back to the active market's bucket_seconds (5m → 60s, 15m → 60s, 1h → 300s, 4h → 600s). Allowed values are per-market; the dropdown filters to them.",
  skip_first_candles: "Drop the lowest-index N qualifying candles before evaluation. Useful on long windows to ignore early warm-up noise. 0 disables.",
  open_price_cushion_min_bps: "Minimum cushion (basis points of the window open) the current price must be BEYOND the open in the bet direction before entering. e.g. 2 = 0.02% (~$15 on a $77k BTC window, ~$0.48 on a $2.4k ETH window). Applies only when the Cushion Gate is on.",
  ...KRONOS_FIELD_DESCRIPTIONS,
  ...COUNTERPART_FIELD_DESCRIPTIONS,
  ...FAVORITE_HOLD_FIELD_DESCRIPTIONS,
  // Plain-English copy wins over the older technical text above.
  ...FIELD_COPY,
};

function simEffectiveTimeframeSeconds(configValue: number, marketBucketSeconds: number): number {
  return configValue && configValue > 0 ? configValue : Math.max(60, marketBucketSeconds);
}

// Friendly labels for keys whose snake-case default is unclear.
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  compounding_stop_after_wins: "Stop Compounding After X Wins",
  compounding_stop_size_stay: "Keep compounded size after stop",
  compounding_resume_from_saved: "Resume from saved size after loss",
  momentum_sell_window_type: "Window Type",
  momentum_sell_window_value: "Window Size",
  momentum_sell_sl_enabled: "Enable SL Sub-mode",
  momentum_sell_sl_threshold_type: "SL Threshold Type",
  momentum_sell_sl_threshold_value: "SL Threshold Value",
  momentum_sell_sl_require_seconds: "SL Require Seconds",
  momentum_sell_sl_start_at_seconds_left: "SL Start at Seconds Left",
  momentum_sell_tp_enabled: "Enable TP Sub-mode",
  momentum_sell_tp_threshold_type: "TP Threshold Type",
  momentum_sell_tp_threshold_value: "TP Threshold Value",
  momentum_sell_tp_min_seconds_below: "TP Min Seconds Below",
  momentum_sell_tp_start_at_seconds_left: "TP Start at Seconds Left",
  momentum_sell_profit_is_win: "Count profitable sells as wins",
  momentum_sell_loss_breaks_streak: "Loss-side sell breaks win streak",
  loss_streak_continue_threshold_usdc: "Win Streak Max Loss Threshold (USDC)",
  allow_multiple_orders_per_window: "Allow Multiple Orders per Window",
  entry_confirm_seconds: "Entry Confirmation Delay (seconds)",
  sim_execution_delay_seconds: "Simulated Order Placement Delay (seconds)",
  max_drop_from_peak_odds: "Max Drop From Peak Odds",
  min_price_excursion_retention: "Min Price Excursion Retention (0.0 – 1.0)",
  vol_cushion_min_z: "Min Cushion z-score",
  underlying_velocity_lookback_seconds: "Velocity Lookback (seconds)",
  underlying_velocity_min_bps: "Min Velocity (bps toward pick)",
  open_side_streak_seconds: "Open-Side Streak (seconds)",
  max_open_crosses: "Max Open Crossings",
  max_spread: "Max Bid-Ask Spread",
  counterpart_confirm_mode: "Confirmation Mode",
  counterpart_confirm_max_opposing_bps: "Max Opposing Counterpart Move (bps)",
  kronos_min_edge_over_ask: "Min Edge Over Entry Ask",
  exit_underlying_breach_bps: "Breach Threshold (bps from open)",
  candle_timeframe_seconds: "Candle Timeframe",
  skip_first_candles: "Skip First Candles",
  live_candle_minute: "Live Candle Index",
  prev_windows_count: "Windows to Check (N)",
  prev_windows_min_matches: "Required Matches (M)",
  open_price_cushion_min_bps: "Min Cushion (bps of open)",
  pre_decision_avg_odds_min: "Min Frozen Average Odds (0.0 – 1.0)",
  band_dwell_min_seconds: "Min Time in Range (seconds)",
  band_dwell_require_exceeds_opposite: "Require Time in Range > Opposite Side",
  flip_gate_max_flips: "Max Flips",
  flip_gate_skip_seconds: "Skip First (seconds)",
  flip_gate_flip_threshold: "Flip Threshold (0.0 – 1.0)",
  ...FAVORITE_HOLD_FIELD_LABELS,
};

function formatFieldLabel(key: string): string {
  const override = FIELD_LABEL_OVERRIDES[key];
  if (override) return override;
  return key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

/**
 * V3 multi-market: synthetic slugs use one of two shapes (see
 * ``contracts/markets.json`` and migration 0007):
 *   1. ``*-{epoch}`` where epoch is a unix-second integer (e.g.
 *      ``btc-updown-15m-1782625800``) — the current format for EVERY
 *      market post-0007, including btc-5m / eth-5m.
 *   2. ``*-{yyyymmdd}-{hhmm}z`` — the legacy btc-5m / eth-5m shape from
 *      before migration 0007 unified them with ``slug_pattern``. Kept
 *      as a fallback so any DB row that was never renamed (e.g. from a
 *      stale snapshot import) still parses correctly.
 *
 * V2 only ever saw shape (1), so the previous parser just
 * `Number(last)`-ed the final segment and returned NaN -> ``null`` for
 * the yyyymmdd-hhmm shape, which is why the Order Window column
 * rendered the raw slug for legacy 5-minute exports.
 */
function windowStartFromSlug(slug: string): Date | null {
  const parts = slug.split("-");
  if (parts.length < 2) return null;
  const last = parts[parts.length - 1] ?? "";
  const prev = parts[parts.length - 2] ?? "";

  // Shape 1: trailing ``YYYYMMDD-HHMMz`` (5-minute synthetic slugs).
  const hhmmZ = last.match(/^(\d{2})(\d{2})z$/i);
  const yyyymmdd = prev.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (hhmmZ && yyyymmdd) {
    const [, y, mo, d] = yyyymmdd;
    const [, hh, mm] = hhmmZ;
    const iso = `${y}-${mo}-${d}T${hh}:${mm}:00Z`;
    const dt = new Date(iso);
    return Number.isFinite(dt.getTime()) ? dt : null;
  }

  // Shape 2: trailing ``{epoch}`` unix-second integer.
  const sec = Number(last);
  if (Number.isFinite(sec) && sec > 0) {
    const dt = new Date(sec * 1000);
    return Number.isFinite(dt.getTime()) ? dt : null;
  }
  return null;
}

/**
 * V3 multi-market: ``windowSeconds`` defaults to 300 so V2 callers that
 * have not been threaded through with a `MarketKind` keep printing
 * 5-minute end labels (e.g. "2:00-2:05AM"). Pass the active market's
 * `window_seconds` to get correct end labels for 15m/1h/4h markets.
 */
function formatWindowTitle(start: Date, windowSeconds: number = 300): string {
  const end = new Date(start.getTime() + Math.max(1, windowSeconds) * 1000);
  const fmt = (d: Date) => {
    const hours24 = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${hours12}:${minutes}`;
  };
  const suffix = end.getHours() >= 12 ? "PM" : "AM";
  return `${fmt(start)}-${fmt(end)}${suffix}`;
}

function formatDateSeparator(d: Date): string {
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "long" });
  const year = d.getFullYear();
  const mod100 = day % 100;
  const suffix = mod100 >= 11 && mod100 <= 13
    ? "th"
    : day % 10 === 1
      ? "st"
      : day % 10 === 2
        ? "nd"
        : day % 10 === 3
          ? "rd"
          : "th";
  return `${month} ${day}${suffix}, ${year}`;
}

function augmentConfigWithDefaults(raw: Record<string, unknown>): Record<string, unknown> {
  const cfgWithMomentum = Object.prototype.hasOwnProperty.call(raw, "odds_momentum_use_unique_odds")
    ? raw
    : { ...raw, odds_momentum_use_unique_odds: true };
  const cfg: Record<string, unknown> = { ...cfgWithMomentum };
  const setIfMissing = (key: string, value: unknown) => {
    if (!Object.prototype.hasOwnProperty.call(cfg, key)) cfg[key] = value;
  };
  setIfMissing("odds_momentum_avg_lookback", 0);
  setIfMissing("odds_momentum_avg_max_diff", 0.5);
  setIfMissing("compounding_enabled", false);
  setIfMissing("compounding_win_pct", 0);
  setIfMissing("compounding_loss_pct", 0);
  setIfMissing("compounding_max_size_usdc", 0);
  setIfMissing("compounding_stop_after_wins", cfg.compounding_reset_after_wins ?? 0);
  setIfMissing("compounding_stop_size_stay", false);
  setIfMissing("compounding_resume_from_saved", false);
  setIfMissing("cascading_sizes_enabled", false);
  setIfMissing("cascading_sizes", "10.0,7.0,5.0");
  setIfMissing("allow_live_candle", false);
  setIfMissing("live_candle_minute", 0);
  // V3 multi-market candle controls. ``0`` for both means "use market
  // bucket_seconds" / "no skip" so legacy configs replay identical.
  setIfMissing("candle_timeframe_seconds", 0);
  setIfMissing("skip_first_candles", 0);
  setIfMissing("open_price_gate_enabled", false);
  setIfMissing("open_price_override_enabled", false);
  setIfMissing("open_price_override_within_seconds", 0);
  setIfMissing("open_price_cushion_enabled", false);
  setIfMissing("open_price_cushion_min_bps", 2.0);
  setIfMissing("prev_windows_check_enabled", false);
  setIfMissing("prev_windows_count", 3);
  setIfMissing("prev_windows_min_matches", 2);
  setIfMissing("pre_decision_avg_odds_enabled", false);
  setIfMissing("pre_decision_avg_odds_min", 0.53);
  setIfMissing("band_dwell_enabled", false);
  setIfMissing("band_dwell_min_seconds", 0);
  setIfMissing("band_dwell_require_exceeds_opposite", false);
  setIfMissing("flip_gate_enabled", false);
  setIfMissing("flip_gate_max_flips", 0);
  setIfMissing("flip_gate_skip_seconds", 0);
  setIfMissing("flip_gate_flip_threshold", 0.5);
  setIfMissing("kronos_check_enabled", false);
  setIfMissing("kronos_require_direction", true);
  setIfMissing("kronos_min_confidence", 0.5);
  setIfMissing("kronos_model", "small");
  setIfMissing("kronos_lookback", 400);
  setIfMissing("kronos_candle_timeframe_seconds", 60);
  setIfMissing("kronos_sample_count", 5);
  setIfMissing("kronos_temperature", 1.0);
  setIfMissing("kronos_top_p", 0.9);
  setIfMissing("momentum_sell_enabled", false);
  setIfMissing("momentum_sell_window_type", "number");
  setIfMissing("momentum_sell_window_value", 10);
  setIfMissing("momentum_sell_sl_enabled", true);
  setIfMissing("momentum_sell_sl_threshold_type", "points");
  setIfMissing("momentum_sell_sl_threshold_value", 0.05);
  setIfMissing("momentum_sell_sl_require_seconds", 0);
  setIfMissing("momentum_sell_sl_start_at_seconds_left", 0);
  setIfMissing("momentum_sell_tp_enabled", false);
  setIfMissing("momentum_sell_tp_threshold_type", "points");
  setIfMissing("momentum_sell_tp_threshold_value", 0.05);
  setIfMissing("momentum_sell_tp_min_seconds_below", 3);
  setIfMissing("momentum_sell_tp_start_at_seconds_left", 0);
  setIfMissing("momentum_sell_profit_is_win", true);
  setIfMissing("momentum_sell_loss_breaks_streak", true);
  setIfMissing("loss_streak_continue_threshold_usdc", 0);
  setIfMissing("no_sell_window_seconds", 0);
  setIfMissing("allow_multiple_orders_per_window", false);
  // Candle gate master toggle: older snapshots have no field -> treat as
  // enabled (the engines default it to true for byte-identical replay).
  setIfMissing("candle_filter_enabled", true);
  setIfMissing("entry_confirm_seconds", 0);
  setIfMissing("sim_execution_delay_seconds", 0);
  setIfMissing("peak_drawdown_gate_enabled", false);
  setIfMissing("max_drop_from_peak_odds", 0.06);
  setIfMissing("min_price_excursion_retention", 0);
  setIfMissing("vol_cushion_gate_enabled", false);
  setIfMissing("vol_cushion_min_z", 1.5);
  setIfMissing("underlying_velocity_gate_enabled", false);
  setIfMissing("underlying_velocity_lookback_seconds", 15);
  setIfMissing("underlying_velocity_min_bps", 0);
  setIfMissing("open_side_gate_enabled", false);
  setIfMissing("open_side_streak_seconds", 60);
  setIfMissing("max_open_crosses", 0);
  setIfMissing("max_spread_gate_enabled", false);
  setIfMissing("max_spread", 0.03);
  setIfMissing("counterpart_confirm_gate_enabled", false);
  setIfMissing("counterpart_confirm_mode", "price");
  setIfMissing("counterpart_confirm_max_opposing_bps", 0);
  setIfMissing("kronos_min_edge_over_ask", 0);
  setIfMissing("exit_require_underlying_breach", false);
  setIfMissing("exit_underlying_breach_bps", 0);
  setIfMissing("exit_order_type", "INHERIT");
  setIfMissing("exit_slippage_ticks", -1);
  setIfMissing("pre_order_metrics_enabled", false);
  setIfMissing("pre_order_up_avg_odds_filter_enabled", false);
  setIfMissing("pre_order_up_avg_odds_min", 0);
  setIfMissing("pre_order_up_avg_odds_max", 1);
  setIfMissing("pre_order_down_avg_odds_filter_enabled", false);
  setIfMissing("pre_order_down_avg_odds_min", 0);
  setIfMissing("pre_order_down_avg_odds_max", 1);
  setIfMissing("pre_order_up_min_odds_filter_enabled", false);
  setIfMissing("pre_order_up_min_odds_min", 0);
  setIfMissing("pre_order_up_min_odds_max", 1);
  setIfMissing("pre_order_up_min_pct_filter_enabled", false);
  setIfMissing("pre_order_up_min_pct_min", -100);
  setIfMissing("pre_order_up_min_pct_max", 100);
  setIfMissing("pre_order_down_min_odds_filter_enabled", false);
  setIfMissing("pre_order_down_min_odds_min", 0);
  setIfMissing("pre_order_down_min_odds_max", 1);
  setIfMissing("pre_order_down_max_pct_filter_enabled", false);
  setIfMissing("pre_order_down_max_pct_min", -100);
  setIfMissing("pre_order_down_max_pct_max", 100);
  setIfMissing("gtc_auto_cancel_on_drift", true);
  setIfMissing("gtc_recheck_seconds", 2);
  setIfMissing("gtc_cancel_unfilled_after_seconds", 15);
  setIfMissing("trade_days_enabled", false);
  setIfMissing("trade_days_allowed", "MON,TUE,WED,THU,FRI,SAT,SUN");
  // Open-price direction feature (V3): disabled defaults for pre-feature snapshots.
  setIfMissing("open_price_gate_enabled", false);
  setIfMissing("open_price_override_enabled", false);
  setIfMissing("open_price_override_within_seconds", 0);
  setIfMissing("open_price_cushion_enabled", false);
  setIfMissing("open_price_cushion_min_bps", 2.0);
  // Counterpart-market override: disabled / no-op defaults so pre-feature
  // snapshots surface the controls and replay byte-identical.
  setIfMissing("counterpart_override_enabled", false);
  setIfMissing("counterpart_odds_threshold", 0.95);
  setIfMissing("counterpart_decide_within_pct", 0);
  setIfMissing("counterpart_stable_seconds", 0);
  setIfMissing("counterpart_require_trend", true);
  setIfMissing("counterpart_follow_direction", false);
  setIfMissing("counterpart_follow_exclusive", false);
  setIfMissing("counterpart_bypass_candle", true);
  setIfMissing("counterpart_bypass_open_price", false);
  setIfMissing("counterpart_bypass_prev_windows", false);
  setIfMissing("counterpart_bypass_kronos", false);
  setIfMissing("counterpart_bypass_momentum", false);
  setIfMissing("counterpart_bypass_pre_order", false);
  setIfMissing("counterpart_bypass_time_ranges", false);
  setIfMissing("counterpart_bypass_min_odds", false);
  setIfMissing("counterpart_bypass_max_odds", false);
  // Counterpart matching-order (sub-feature): disabled / no-op defaults.
  setIfMissing("counterpart_match_order_enabled", false);
  setIfMissing("counterpart_match_order_require_trend", false);
  setIfMissing("counterpart_match_order_require_candle", false);
  setIfMissing("counterpart_match_order_require_open_price", false);
  setIfMissing("counterpart_match_order_require_prev_windows", false);
  setIfMissing("counterpart_match_order_require_kronos", false);
  setIfMissing("counterpart_match_order_require_momentum", false);
  setIfMissing("counterpart_match_order_require_pre_order", false);
  setIfMissing("counterpart_match_order_require_time_ranges", false);
  setIfMissing("counterpart_match_order_require_min_odds", false);
  setIfMissing("counterpart_match_order_require_max_odds", false);

  setIfMissing("favorite_hold_enabled", false);
  setIfMissing("favorite_hold_flip_threshold", 0.51);
  setIfMissing("favorite_hold_skip_seconds", 0);
  setIfMissing("favorite_hold_tier1_enabled", false);
  setIfMissing("favorite_hold_tier1_min_odds", 0.8);
  setIfMissing("favorite_hold_tier1_dwell_seconds", 60);
  setIfMissing("favorite_hold_tier2_enabled", false);
  setIfMissing("favorite_hold_tier2_min_rise", 0.1);
  setIfMissing("favorite_hold_tier2_check_seconds", 120);
  setIfMissing("favorite_hold_tier2_require_candle", false);
  setIfMissing("favorite_hold_tier2_require_open_price", false);
  setIfMissing("favorite_hold_tier2_require_prev_windows", false);
  setIfMissing("favorite_hold_tier2_require_kronos", false);
  setIfMissing("favorite_hold_tier2_require_momentum", false);
  setIfMissing("favorite_hold_tier2_require_pre_order", false);
  setIfMissing("favorite_hold_tier2_require_time_ranges", false);
  setIfMissing("favorite_hold_tier2_require_min_odds", false);
  setIfMissing("favorite_hold_tier3_enabled", false);
  setIfMissing("favorite_hold_tier3_bypass_candle", false);
  setIfMissing("favorite_hold_tier3_bypass_open_price", false);
  setIfMissing("favorite_hold_tier3_bypass_prev_windows", false);
  setIfMissing("favorite_hold_tier3_bypass_kronos", false);
  setIfMissing("favorite_hold_tier3_bypass_momentum", false);
  setIfMissing("favorite_hold_tier3_bypass_pre_order", false);
  setIfMissing("favorite_hold_tier3_bypass_time_ranges", false);
  setIfMissing("favorite_hold_tier3_bypass_min_odds", false);
  setIfMissing("hedge_enabled", false);
  setIfMissing("hedge_pct", 0);
  return cfg;
}

function parseImportedConfigVersionIdFromUrl(): string | null {
  const hash = window.location.hash || "";
  const qIdx = hash.indexOf("?");
  if (qIdx < 0) return null;
  const params = new URLSearchParams(hash.slice(qIdx + 1));
  const v = params.get("config_version_id");
  return v && v.trim() ? v.trim() : null;
}

function parseImportedStateFromUrl(): {
  settings: Record<string, unknown> | null;
  from: string | null;
  to: string | null;
} {
  const hash = window.location.hash || "";
  const qIdx = hash.indexOf("?");
  if (qIdx < 0) {
    return { settings: null, from: null, to: null };
  }
  const query = hash.slice(qIdx + 1);
  const params = new URLSearchParams(query);
  const from = params.get("import_from");
  const to = params.get("import_to");
  const raw = params.get("import_settings");
  if (!raw) {
    return {
      settings: null,
      from: from && from.trim() ? from : null,
      to: to && to.trim() ? to : null,
    };
  }
  try {
    const decoded = JSON.parse(raw) as unknown;
    if (!decoded || typeof decoded !== "object" || Array.isArray(decoded)) {
      return {
        settings: null,
        from: from && from.trim() ? from : null,
        to: to && to.trim() ? to : null,
      };
    }
    return {
      settings: decoded as Record<string, unknown>,
      from: from && from.trim() ? from : null,
      to: to && to.trim() ? to : null,
    };
  } catch {
    return {
      settings: null,
      from: from && from.trim() ? from : null,
      to: to && to.trim() ? to : null,
    };
  }
}

function mergeConfigInputsFromImportedSettings(args: {
  baseConfig: Record<string, unknown>;
  imported: Record<string, unknown>;
}): Record<string, string> {
  const mapped: Record<string, string> = {
    ...Object.fromEntries(
      Object.entries(args.baseConfig)
        .filter(([k]) => !HIDDEN_FIELDS.has(k) && EDITABLE_KEYS.has(k))
        .map(([k, v]) => [k, valueToInput(v)]),
    ),
    trade_time_ranges_split_results: "false",
    trade_days_split_results: "false",
  };
  for (const [key, value] of Object.entries(args.imported)) {
    if (HIDDEN_FIELDS.has(key) || (!EDITABLE_KEYS.has(key) && !(SIMULATION_ONLY_KEYS as readonly string[]).includes(key))) continue;
    mapped[key] = valueToInput(value);
  }
  return mapped;
}

function downloadConfigJson(
  configOverride: Record<string, unknown>,
  configTemplate: Record<string, unknown> | null,
) {
  const payload = { ...(configTemplate ?? {}), ...configOverride };
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const name = String(payload.config_name ?? "config").replace(/[^a-z0-9_-]+/gi, "_") || "config";
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}-${ts}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ManualSimulatorPage() {
  // V3 multi-market: bind manual simulations to the market currently
  // selected in the global dropdown so the data-api restricts the
  // dataset to that market's windows. Without this, the data-api falls
  // back to btc-5m (V2 default), and selecting ETH 5m in the UI silently
  // simulates against BTC 5m windows.
  const marketId = useMarketId();
  // V3 multi-market: surface the active market's `window_seconds` so
  // window titles, secs-left ladders, and minute-tab counts in the
  // result panel scale per-market instead of pinning to btc-5m's 5×60s
  // shape.
  const { market: activeMarket } = useMarket();
  const marketWindowSeconds = Math.max(1, Number(activeMarket?.window_seconds) || 300);
  const marketBucketSeconds = Math.max(1, Number(activeMarket?.bucket_seconds) || 60);
  const marketBucketCount = Math.max(
    1,
    Math.floor(marketWindowSeconds / marketBucketSeconds),
  );
  // V3 multi-market: for any market > 15 min, slice the window-detail view
  // into 5-minute buckets and gate every heavy query (`/v2/windows/:slug/
  // ticks` + the outcome-trace re-run) on the user explicitly picking one.
  // The native `bucket_seconds` (300s on 1h, 600s on 4h) makes individual
  // buckets too large to fetch on auto-load, so the UI overrides it to a
  // uniform 5-minute view here. 5m + 15m markets are small enough to
  // auto-load and keep using their native bucket size.
  const lazyLoadDetail = marketWindowSeconds > 900;
  // ``viewBucketSeconds`` / ``viewBucketCount`` are recomputed further
  // down (after ``selectedSliceLengthSec`` is known) so the Price feed
  // table grid resolution matches whichever pill leaf the user picked
  // (5-min default, 1-min when refined).
  // V3 multi-market: pull the active config for the currently selected
  // market (rather than the global/legacy active pointer) so that
  // switching the market dropdown can swap the form to that market's
  // active config when one exists.
  const activeCfgQ = useQuery({
    queryKey: ["markets", marketId, "config-active", "for-sim"],
    queryFn: () => api.markets.activeConfig(marketId),
    staleTime: 30_000,
    enabled: Boolean(marketId),
  });
  // Track which market we've already applied an active-config swap for
  // so the effect below fires once per market change (and on initial
  // load) without clobbering the operator's edits across re-renders.
  const [appliedMarketId, setAppliedMarketId] = useState<string | null>(null);

  const versionsQ = useQuery({
    queryKey: ["config", "versions", "for-manual-sim"],
    queryFn: async () => {
      const page = await api.config.versions({ limit: 200 });
      return page.items;
    },
    staleTime: 30_000,
  });

  const [selectedConfigVersionId, setSelectedConfigVersionId] = useState<string | null>(
    () => parseImportedConfigVersionIdFromUrl(),
  );
  // V3 multi-market: remember the URL-imported ``?config_version_id=``
  // (if any) so the market-switch effect below doesn't clobber the
  // operator's deep-link with the current market's active config on
  // first mount. Cleared once the operator picks a different id.
  const urlImportedConfigIdRef = useRef<string | null>(parseImportedConfigVersionIdFromUrl());
  const [configInputs, setConfigInputs] = useState<Record<string, string>>({});
  const [configTemplate, setConfigTemplate] = useState<Record<string, unknown> | null>(null);
  // Feature column layout: collapsed cards persist per page; "added" cards
  // are features the operator pulled in (or switched off) that should stay
  // visible even while their config value says inactive.
  const { collapsed: collapsedPanels, toggle: toggleCollapsed, setAll: setAllCollapsed, expand: expandPanel, collapse: collapsePanel } =
    useCollapsedPanels("manual-sim:collapsed-panels");
  const [addedPanels, setAddedPanels] = useState<string[]>([]);
  // Last non-zero value of each value-driven feature, restored when its
  // toggle is switched back on.
  const [stashedValues, setStashedValues] = useState<Record<string, string>>({});
  const [from, setFrom] = useState(DEFAULT_SIM_FROM);
  const [to, setTo] = useState("");
  const [subtractNoFillsFromResults, setSubtractNoFillsFromResults] = useState(false);
  const [excludeStaleOdds, setExcludeStaleOdds] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [selectedOutcomeSlug, setSelectedOutcomeSlug] = useState<string | null>(null);
  const [showResultList, setShowResultList] = useState(false);
  const [displaySimulatedWindowAnalytics, setDisplaySimulatedWindowAnalytics] = useState(false);
  const [uncheckedRanges, setUncheckedRanges] = useState<Set<string>>(new Set());
  // Last-known per-range stats keyed by range label. Merged across sim
  // re-runs so ranges the user unchecked (and therefore excluded from the
  // simulated ``trade_time_ranges``) remain visible in the breakdown with
  // their most recent stats, letting them be re-enabled later.
  const [rangeStatsHistory, setRangeStatsHistory] = useState<Map<string, SimRangeBreakdownRow>>(new Map());
  const [uncheckedDays, setUncheckedDays] = useState<Set<string>>(new Set());
  // Last-known per-day stats keyed by day label. Merged across re-runs so
  // days the user unchecked (and therefore excluded from the simulated
  // ``trade_days_allowed``) remain visible in the breakdown with their most
  // recent stats, letting them be re-enabled later.
  const [dayStatsHistory, setDayStatsHistory] = useState<Map<string, SimDayBreakdownRow>>(new Map());
  // V3 multi-market: bucket selection state. The pill picker is used
  // for every window length (5m / 15m / 1h / 4h). ``lazyHour`` /
  // ``lazyQuarter`` / ``lazyFiveMin`` / ``lazyOneMin`` are the four
  // tiers; each is suppressed when its span doesn't fit inside the
  // active market's window (e.g. 5m markets only render the 1-min
  // row; 1h markets skip the Hour row). The prefix is historical --
  // these used to gate ONLY the lazy-loaded 1h / 4h surface but now
  // back the entire picker.
  const [lazyHour, setLazyHour] = useState<number | null>(null);
  const [lazyQuarter, setLazyQuarter] = useState<number | null>(null);
  const [lazyFiveMin, setLazyFiveMin] = useState<number | null>(null);
  const [lazyOneMin, setLazyOneMin] = useState<number | null>(null);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);

  // Reset the checked/unchecked selection and the stats history only when
  // the simulated context genuinely changes (config version or market) —
  // NOT on every re-run. This keeps previously-unchecked ranges visible so
  // the operator can re-enable them after re-running the same config.
  useEffect(() => {
    setUncheckedRanges(new Set());
    setRangeStatsHistory(new Map());
    setUncheckedDays(new Set());
    setDayStatsHistory(new Map());
  }, [selectedConfigVersionId, marketId]);

  const versionOptions = useMemo(() => {
    const activeId = activeCfgQ.data?.active?.config_version_id;
    return (versionsQ.data ?? []).map((v) => {
      const name = typeof v.config?.config_name === "string" && v.config.config_name.trim()
        ? v.config.config_name.trim()
        : v.config_version_id;
      const activeSuffix = v.config_version_id === activeId ? " · active" : "";
      return {
        value: v.config_version_id,
        label: `${name}${activeSuffix}`,
      };
    });
  }, [versionsQ.data, activeCfgQ.data?.active?.config_version_id]);

  const selectedVersion = useMemo(
    () => (versionsQ.data ?? []).find((v) => v.config_version_id === selectedConfigVersionId) ?? null,
    [versionsQ.data, selectedConfigVersionId],
  );

  // V3 multi-market: when the operator switches markets via the
  // global dropdown, swap the form to that market's active config if
  // one exists. When the new market has no active config, leave the
  // form untouched (per product requirement: "the forms values stay
  // the same from what they were previously"). The ``appliedMarketId``
  // gate ensures this effect runs once per market change rather than
  // re-applying on every re-render.
  useEffect(() => {
    // Wait until the per-market query has loaded data for the CURRENT
    // marketId (TanStack may still be returning the previous market's
    // data while the new request is in flight).
    if (!activeCfgQ.data || activeCfgQ.data.market_id !== marketId) return;
    if (appliedMarketId === marketId) return;

    // V3: honour ``?config_version_id=X`` deep-link on first mount so
    // opening a snapshot from the Previous Configs page actually
    // prefills the form with THAT snapshot instead of the market's
    // current active config. Once the versions list has loaded and
    // confirmed the id is real, we mark this market as applied and
    // stop deferring.
    const importedId = urlImportedConfigIdRef.current;
    if (importedId && selectedConfigVersionId === importedId) {
      // Deep-link pending. Don't let the active-config swap clobber it.
      // The versions list can resolve AFTER activeCfgQ (a race), so we
      // must wait for it before deciding: if the id is real, keep it and
      // mark the market applied; only fall through to the market's active
      // config once we've confirmed the list loaded AND the id isn't in it.
      if (!versionsQ.data) return;
      if (versionsQ.data.some((v) => v.config_version_id === importedId)) {
        setAppliedMarketId(marketId);
        return;
      }
    }

    const activeId = activeCfgQ.data.active?.config_version_id ?? null;
    if (activeId) {
      setSelectedConfigVersionId(activeId);
    } else if (!selectedConfigVersionId) {
      // First load AND this market has no active config: fall back to
      // the most recent version so the form is at least populated.
      const first = versionsQ.data?.[0]?.config_version_id;
      if (first) setSelectedConfigVersionId(first);
    }
    // else: no active config for the new market, but the operator
    // already has a selection -- keep it so their current form values
    // survive the market switch.
    setAppliedMarketId(marketId);
  }, [marketId, activeCfgQ.data, appliedMarketId, selectedConfigVersionId, versionsQ.data]);

  useEffect(() => {
    if (!selectedVersion) return;
    const cfg = augmentConfigWithDefaults((selectedVersion.config ?? {}) as Record<string, unknown>);
    const importedState = parseImportedStateFromUrl();
    const nextInputs = importedState.settings
      ? mergeConfigInputsFromImportedSettings({ baseConfig: cfg, imported: importedState.settings })
      : {
        ...Object.fromEntries(
          Object.entries(cfg)
            .filter(([k]) => !HIDDEN_FIELDS.has(k) && EDITABLE_KEYS.has(k))
            .map(([k, v]) => [k, valueToInput(v)]),
        ),
        trade_time_ranges_split_results: "false",
        trade_days_split_results: "false",
      };
    setConfigTemplate(cfg);
    setConfigInputs(nextInputs);
    if (importedState.from) setFrom(importedState.from);
    if (importedState.to) setTo(importedState.to);
    setAddedPanels([]);

    if (importedState.settings || importedState.from || importedState.to) {
      const currentHash = window.location.hash || "";
      const cleanHash = currentHash.includes("?")
        ? currentHash.slice(0, currentHash.indexOf("?"))
        : currentHash;
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${cleanHash}`);
      notifications.show({
        title: "Settings imported",
        message: "Applied result settings to Manual Simulator form.",
        color: "green",
      });
    }
  }, [selectedVersion?.config_version_id]);

  const configOverride = useMemo(() => {
    if (!configTemplate) return null;
    const baseEntries = Object.keys(configTemplate).map((key) => {
      if (HIDDEN_FIELDS.has(key)) return [key, configTemplate[key]];
      return [key, inputToValue(configInputs[key] ?? valueToInput(configTemplate[key]), configTemplate[key])];
    });
    const simOnlyEntries = SIMULATION_ONLY_KEYS
      .filter((key) => Object.prototype.hasOwnProperty.call(configInputs, key))
      .map((key) => [key, inputToValue(configInputs[key] ?? "false", false)]);
    return Object.fromEntries([...baseEntries, ...simOnlyEntries]);
  }, [configTemplate, configInputs]);

  const showWindowAnalytics = showResultList && displaySimulatedWindowAnalytics;

  const runQ = useQuery({
    queryKey: ["simulations", runId],
    queryFn: () => api.simulations.get(runId!),
    enabled: Boolean(runId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && FINAL_STATUSES.has(status) ? false : 1_500;
    },
  });

  const runMut = useMutation({
    mutationFn: () => {
      const tradeTimeRanges =
        configOverride && typeof configOverride.trade_time_ranges === "string"
          ? configOverride.trade_time_ranges
          : "";
      assertTradeTimeRangesMax(tradeTimeRanges);
      return api.simulations.run({
        mode: "single",
        config_override: configOverride ?? undefined,
        from: toIsoOrUndef(from),
        to: toIsoOrUndef(to),
        subtract_no_fills_from_results: subtractNoFillsFromResults,
        exclude_stale_odds: excludeStaleOdds,
        market_id: marketId,
      });
    },
    onSuccess: (run) => {
      setRunId(run.run_id);
      notifications.show({
        title: "Manual simulation started",
        message: `Run ${run.run_id} is ${run.status}.`,
        color: "blue",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to start manual simulation",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => api.simulations.cancel(runId!),
    onSuccess: () => {
      runQ.refetch();
      notifications.show({ title: "Simulation cancelled", message: `Run ${runId} was stopped.`, color: "gray" });
    },
    onError: (error) =>
      notifications.show({ title: "Failed to cancel simulation", message: extractErrorMessage(error), color: "red" }),
  });

  const saveMut = useMutation({
    mutationFn: (saved: boolean) => api.simulations.setSaved(runId!, saved),
    onSuccess: (_, saved) => {
      runQ.refetch();
      notifications.show({
        title: saved ? "Saved to Saved Simulations" : "Removed from Saved Simulations",
        message: saved
          ? "This manual run will appear in the Saved Simulations list."
          : "This manual run was unsaved.",
        color: saved ? "green" : "gray",
      });
    },
    onError: (error) =>
      notifications.show({
        title: "Failed to update saved state",
        message: extractErrorMessage(error),
        color: "red",
      }),
  });

  const discardMut = useMutation({
    mutationFn: () => api.simulations.delete(runId!),
    onSuccess: () => {
      const droppedId = runId;
      setRunId(null);
      setShowResultList(false);
      notifications.show({
        title: "Results discarded",
        message: `Run ${droppedId} was deleted.`,
        color: "gray",
      });
    },
    onError: (error) =>
      notifications.show({
        title: "Failed to discard results",
        message: extractErrorMessage(error),
        color: "red",
      }),
  });

  const summary = useMemo(() => toSummaryView(runQ.data?.result_summary), [runQ.data?.result_summary]);
  // Fold each completed run's per-range stats into the history map so
  // unchecked ranges (absent from the freshly-simulated breakdown) keep
  // their last-known stats on screen.
  const breakdownKey = summary?.time_range_breakdown
    .map((r) => `${r.range}:${r.net_pnl_usdc}:${r.windows_with_bet}`)
    .join("|");
  useEffect(() => {
    if (!summary || summary.time_range_breakdown.length === 0) return;
    setRangeStatsHistory((prev) => {
      const next = new Map(prev);
      for (const row of summary.time_range_breakdown) next.set(row.range, row);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdownKey]);
  // The full set of ranges to render: the current run's breakdown plus any
  // previously-seen ranges the operator unchecked (kept with their last
  // stats). Sorted by range label so ordering is stable regardless of which
  // ranges are currently active.
  const displayRangeBreakdown = useMemo(() => {
    const rows: SimRangeBreakdownRow[] = [...(summary?.time_range_breakdown ?? [])];
    const present = new Set(rows.map((r) => r.range));
    for (const [range, row] of rangeStatsHistory) {
      if (!present.has(range)) rows.push(row);
    }
    return rows.sort((a, b) => a.range.localeCompare(b.range));
  }, [summary?.time_range_breakdown, rangeStatsHistory]);
  // Same fold-across-runs treatment for the per-day breakdown so unchecked
  // days stay visible with their last-known stats.
  const dayBreakdownKey = summary?.day_breakdown
    .map((r) => `${r.day}:${r.net_pnl_usdc}:${r.windows_with_bet}`)
    .join("|");
  useEffect(() => {
    if (!summary || summary.day_breakdown.length === 0) return;
    setDayStatsHistory((prev) => {
      const next = new Map(prev);
      for (const row of summary.day_breakdown) next.set(row.day, row);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayBreakdownKey]);
  const displayDayBreakdown = useMemo(() => {
    const rows: SimDayBreakdownRow[] = [...(summary?.day_breakdown ?? [])];
    const present = new Set(rows.map((r) => r.day));
    for (const [day, row] of dayStatsHistory) {
      if (!present.has(day)) rows.push(row);
    }
    return rows.sort((a, b) => dayLabelOrder(a.day) - dayLabelOrder(b.day));
  }, [summary?.day_breakdown, dayStatsHistory]);
  const noFillSubtractionEnabled = useMemo(() => {
    const raw = runQ.data?.result_summary;
    if (!raw || typeof raw !== "object") return false;
    return Boolean((raw as Record<string, unknown>).subtract_no_fills_from_results);
  }, [runQ.data?.result_summary]);
  const staleOddsExcludedEnabled = useMemo(() => {
    const raw = runQ.data?.result_summary;
    if (!raw || typeof raw !== "object") return false;
    return Boolean((raw as Record<string, unknown>).exclude_stale_odds);
  }, [runQ.data?.result_summary]);
  // W/L tallies for orders entered via the counterpart features. An order is
  // a win when its window P/L > 0, a loss when < 0 (mirrors the results-list
  // no-fill subtraction so the tallies match what's shown there).
  const counterpartWL = useMemo(() => {
    const rows = (summary?.outcomes ?? []).filter(
      (o) => !noFillSubtractionEnabled || !o.historical_no_fill,
    );
    const tally = { overrideW: 0, overrideL: 0, matchW: 0, matchL: 0 };
    for (const o of rows) {
      const pnl = Number(o.pnl_usdc || 0);
      if (orderUsedCounterpartOverride(o)) {
        if (pnl > 0) tally.overrideW++;
        else if (pnl < 0) tally.overrideL++;
      }
      if (orderUsedCounterpartMatchOrder(o)) {
        if (pnl > 0) tally.matchW++;
        else if (pnl < 0) tally.matchL++;
      }
    }
    return tally;
  }, [summary, noFillSubtractionEnabled]);
  const orderWindows = useMemo(() => {
    const allowed = new Set<SimOutcomeRow["result"]>([
      "win",
      "loss",
      "skip",
      "take_profit",
      "stop_loss",
      "momentum_sell",
      "unresolved",
    ]);
    const rows = summary?.outcomes.filter(
      (o) => allowed.has(o.result) && (!noFillSubtractionEnabled || !o.historical_no_fill),
    ) ?? [];
    return rows.slice().sort((a, b) => {
      const aMs = windowStartFromSlug(a.slug)?.getTime() ?? Number.NEGATIVE_INFINITY;
      const bMs = windowStartFromSlug(b.slug)?.getTime() ?? Number.NEGATIVE_INFINITY;
      if (aMs !== bMs) return bMs - aMs;
      return b.slug.localeCompare(a.slug);
    });
  }, [summary, noFillSubtractionEnabled]);

  const selectedOutcome = useMemo(
    () => orderWindows.find((o) => o.slug === selectedOutcomeSlug) ?? null,
    [orderWindows, selectedOutcomeSlug],
  );

  const orderWindowItems = useMemo<OrderWindowListItem[]>(() => {
    const out: OrderWindowListItem[] = [];
    let prevDayKey: string | null = null;
    for (const row of orderWindows) {
      const start = windowStartFromSlug(row.slug);
      const title = start ? formatWindowTitle(start, marketWindowSeconds) : row.slug;
      if (start) {
        const dayKey = `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
        if (dayKey !== prevDayKey) {
          out.push({
            type: "date",
            key: `date-${dayKey}`,
            label: formatDateSeparator(start),
          });
          prevDayKey = dayKey;
        }
      }
      out.push({ type: "row", key: row.slug, row, title });
    }
    return out;
  }, [orderWindows]);

  useEffect(() => {
    if (!showWindowAnalytics) {
      setSelectedOutcomeSlug(null);
      return;
    }
    if (orderWindows.length === 0) {
      setSelectedOutcomeSlug(null);
      return;
    }
    if (!selectedOutcomeSlug || !orderWindows.some((o) => o.slug === selectedOutcomeSlug)) {
      setSelectedOutcomeSlug(orderWindows[0]!.slug);
    }
  }, [orderWindows, selectedOutcomeSlug, showWindowAnalytics]);

  // V3 multi-market: clear the bucket selection whenever the user picks
  // a different window so the Price feed + Condition check tables go
  // back to their empty state. Without this, switching from a window
  // where M3 was open would immediately render M3 of the next window
  // (potentially thousands of unrelated rows) before the user even
  // looks at it.
  useEffect(() => {
    setLazyHour(null);
    setLazyQuarter(null);
    setLazyFiveMin(null);
    setLazyOneMin(null);
  }, [selectedOutcomeSlug]);

  // V3 multi-market: bucket-picker UX rules.
  //
  // - Every market uses the hierarchical pill picker. Rows are
  //   added top-down based on what fits inside ``window_seconds``:
  //     * Hour       (only on 4h)
  //     * 15-min     (1h, 4h)
  //     * 5-min      (15m, 1h, 4h)
  //     * 1-min      (always, when hierarchical) -- optional refinement
  //       so a single click loads ~60 ticks instead of ~300.
  //
  // ``lazyLoadDetail`` (defined above) is the SEPARATE flag that decides
  // whether to network-gate the ticks query on a pill selection. It
  // stays at ``> 900s`` (1h, 4h) -- 15m fits in one full-window fetch.
  //
  // V3 multi-market: the pill picker is now used for EVERY window
  // length (matching the Analytics Detail surface). 5m markets used
  // to fall back to a flat ``M1..M5`` Select dropdown; that surface
  // was the only consumer that still rendered a free-form bucket
  // chooser, and it diverged from the rest of the UI. For 5m the
  // pill stack collapses to a single 1-min row (the only tier that
  // fits inside the window).
  const showHourPicker = marketWindowSeconds > 3600;
  const showQuarterPicker = marketWindowSeconds > 900;
  const showFiveMinPicker = marketWindowSeconds > 300;
  const showOneMinPicker = true;
  // True when no outer tier is shown, i.e. the 1-min row IS the only
  // picker (currently only 5m markets). The selection-complete check
  // below treats ``lazyOneMin`` as REQUIRED in this case so the
  // Price-feed and Condition-check tables don't auto-render the
  // full window before the operator picks a slice.
  const oneMinRequired = !showHourPicker && !showQuarterPicker && !showFiveMinPicker;
  const hourOptionCount = Math.max(1, Math.ceil(marketWindowSeconds / 3600));
  // Number of 15-minute quarters inside the active hour. For a 4-hour
  // window every hour has 4 quarters. For sub-hour windows it's the
  // total quarter count.
  const quartersInActiveHour = showHourPicker
    ? 4
    : Math.max(1, Math.ceil(marketWindowSeconds / 900));
  const fiveMinsInActiveQuarter = showQuarterPicker
    ? 3
    : Math.max(1, Math.ceil(marketWindowSeconds / 300));
  const oneMinsInActiveFiveMin = 5; // 5min / 1min, fixed

  const selectedWindowDetailQ = useQuery({
    queryKey: ["analytics", "window", "sim", selectedOutcomeSlug],
    queryFn: () => api.analytics.detail(selectedOutcomeSlug!),
    enabled: Boolean(selectedOutcomeSlug && showWindowAnalytics),
    staleTime: 30_000,
  });
  // V3 multi-market: the lazy-slice math below needs ``window_start`` to
  // convert the selected (hour, quarter, five-min) tuple into ISO
  // timestamps for the API range filter, so hoist this derivation above
  // ``selectedTicksQ`` / ``selectedOutcomeTraceQ``. Other consumers
  // (price-feed grid, condition-check filter) reference the same const
  // further down.
  const selectedWindowStart = selectedWindowDetailQ.data?.window_start ?? "";

  // V3 multi-market: derive the active slice (offset + length in
  // seconds from window_start) from the hierarchical pill picker. The
  // 1-min pill is OPTIONAL when an outer tier (Hour / 15-min / 5-min)
  // is shown -- picking just a 5-min tier shows the full 5-minute
  // bucket; refining with a 1-min pill shrinks the slice to ~60 ticks.
  // When NO outer tier is shown (5m markets), the 1-min pill is
  // REQUIRED so we don't auto-render the full window's 300 rows.
  const hierarchicalSelectionComplete =
    (!showHourPicker || lazyHour != null) &&
    (!showQuarterPicker || lazyQuarter != null) &&
    (!showFiveMinPicker || lazyFiveMin != null) &&
    (!oneMinRequired || lazyOneMin != null);
  const selectedSliceOffsetSec: number | null = (() => {
    if (!hierarchicalSelectionComplete) return null;
    const h = showHourPicker ? (lazyHour ?? 0) : 0;
    const q = showQuarterPicker ? (lazyQuarter ?? 0) : 0;
    const f = showFiveMinPicker ? (lazyFiveMin ?? 0) : 0;
    const m = lazyOneMin ?? 0;
    return h * 3600 + q * 900 + f * 300 + m * 60;
  })();
  const selectedSliceLengthSec = lazyOneMin != null ? 60 : 300;
  // Price-feed grid is bucketed by the active slice length: 60s when
  // the 1-min refinement is set, otherwise the 5-min parent bucket.
  const viewBucketSeconds = selectedSliceLengthSec;
  const viewBucketCount = Math.max(1, Math.floor(marketWindowSeconds / viewBucketSeconds));

  // V3 multi-market: the ticks endpoint returns the whole window in one
  // payload (10k\u2013100k+ rows on 1h/4h markets) and the outcome-trace
  // endpoint re-runs the engine for that window server-side. Both are
  // multi-second operations, so for lazy markets (>15m) we hold them
  // back until the user has picked all the way down to the 1-MINUTE
  // leaf. Requiring the 1-min selection means the eventual ticks fetch
  // ships ~60 rows (not 300) and -- more importantly -- ensures the
  // user has made a definite "yes, run this" gesture before we let the
  // server start an engine re-run that briefly blocks the WS heartbeat.
  // <=15m markets still auto-fetch (small payloads, fast re-run).
  const heavyDetailQueriesEnabled = lazyLoadDetail
    ? lazyOneMin != null && selectedSliceOffsetSec != null
    : true;
  // Compute the ISO timestamps that scope the API requests for lazy
  // markets. ``null`` means "fetch the whole window" (used by non-lazy
  // markets so they keep working as before).
  const selectedSliceRange = (() => {
    if (!lazyLoadDetail || selectedSliceOffsetSec == null) return null;
    const startMs = Date.parse(selectedWindowStart);
    if (!Number.isFinite(startMs)) return null;
    const sliceStartMs = startMs + selectedSliceOffsetSec * 1000;
    const sliceEndMs = sliceStartMs + selectedSliceLengthSec * 1000;
    return {
      from: new Date(sliceStartMs).toISOString(),
      to: new Date(sliceEndMs).toISOString(),
    };
  })();
  const sliceRangeKey = selectedSliceRange
    ? `${selectedSliceRange.from}|${selectedSliceRange.to}`
    : "full";

  const selectedTicksQ = useQuery({
    queryKey: ["windows", "ticks", "sim", selectedOutcomeSlug, sliceRangeKey],
    queryFn: () => api.windows.ticks(selectedOutcomeSlug!, selectedSliceRange ?? {}),
    enabled: Boolean(selectedOutcomeSlug && showWindowAnalytics && heavyDetailQueriesEnabled),
    staleTime: 30_000,
  });

  // V2: fetch the engine's actual check_trace for this single window. The
  // bulk run summary strips check_trace per V2-601, so we re-run the engine
  // for one window via the dedicated endpoint. This makes the displayed
  // condition checks reflect what the engine truly evaluated, with zero
  // dependence on historical bet data or current-form synthesis.
  //
  // V3 multi-market: deliberately FETCH THE FULL WINDOW TRACE (no
  // ``from``/``to`` filter) and filter to the active slice on the
  // client via ``bucketScopedChecks``. The server-side trace endpoint
  // is synchronous: every request re-runs the engine for the whole
  // window on the data-api event loop, which on 4h windows blocks the
  // WS heartbeat long enough for the trading-bot's relay watchdog to
  // flag the bot OFFLINE for a couple seconds. Keying the query by
  // (runId, slug) only means each window inspection triggers AT MOST
  // ONE engine re-run; clicking through the 48 five-min slices is then
  // pure client-side filtering and never disturbs the relay.
  const selectedOutcomeTraceQ = useQuery({
    queryKey: ["sim", "outcomeTrace", runId, selectedOutcomeSlug],
    queryFn: ({ signal }) => api.simulations.outcomeTrace(runId!, selectedOutcomeSlug!, {}, { signal }),
    enabled: Boolean(runId && selectedOutcomeSlug && showWindowAnalytics && heavyDetailQueriesEnabled),
    staleTime: Infinity,
    retry: false,
  });

  const simulatedChecks = useMemo(() => {
    if (!showWindowAnalytics) return [] as SimTraceRow[];
    const cfg = resolveDecisionConfig((configOverride ?? null) as Record<string, unknown> | null);
    const ticks = selectedTicksQ.data?.items ?? [];

    const withMomentum = (rows: SimTraceRow[]) => enrichSimChecksWithMomentum(rows, ticks, cfg);

    // Engine-authoritative trace fetched on-demand from data-api.
    const engineTrace = selectedOutcomeTraceQ.data?.check_trace;
    if (engineTrace && engineTrace.length > 0) {
      const mapped: SimTraceRow[] = engineTrace.map((r) => ({
        check_ts: r.check_ts,
        secs_left: r.secs_left,
        outcome: r.outcome,
        direction: r.direction,
        favorite_odds: r.favorite_odds,
        matching_candles: r.matching_candles,
        candles_evaluated: r.candles_evaluated,
        block_reason: r.block_reason,
        checks: (r.checks as unknown[])
          .filter((c): c is Record<string, unknown> => Boolean(c && typeof c === "object"))
          .map((c) => ({
            name: String(c.name ?? "unknown"),
            passed: typeof c.passed === "boolean" ? c.passed : c.passed == null ? null : Boolean(c.passed),
            value: c.value,
            threshold: c.threshold,
            detail: c.detail == null ? null : String(c.detail),
          })),
      }));
      return withMomentum(mapped.slice().sort((a, b) => Date.parse(a.check_ts) - Date.parse(b.check_ts)));
    }
    if (selectedOutcome?.check_trace?.length) {
      return withMomentum(selectedOutcome.check_trace
        .slice()
        .sort((a, b) => Date.parse(a.check_ts) - Date.parse(b.check_ts)));
    }
    if (!selectedWindowDetailQ.data) return [] as SimTraceRow[];
    const fallbackChecks = synthesizeDecisionChecks(
      selectedWindowDetailQ.data,
      ticks,
      (configOverride ?? null) as Record<string, unknown> | null,
      { simulatorMode: true },
    );
    return withMomentum(fallbackChecks.map(asSimTraceRow));
  }, [
    selectedOutcomeTraceQ.data,
    selectedOutcome?.check_trace,
    selectedWindowDetailQ.data,
    selectedTicksQ.data?.items,
    configOverride,
    showWindowAnalytics,
  ]);

  const placeCheck = useMemo(
    () => findPlaceCheck(simulatedChecks, selectedOutcome?.direction ?? null),
    [simulatedChecks, selectedOutcome?.direction],
  );

  const stopTakeExitTs = useMemo(() => {
    const exit = simulatedChecks.find((row) => row.outcome === "take_profit" || row.outcome === "stop_loss" || row.outcome === "momentum_sell");
    return exit?.check_ts ?? null;
  }, [simulatedChecks]);

  const visibleChecks = useMemo(() => {
    if (simulatedChecks.length === 0 || !selectedOutcome) return [] as SimTraceRow[];
    if (selectedOutcome.result === "skip") {
      return collapseChecksBySecond(simulatedChecks, selectedWindowDetailQ.data?.window_start ?? "");
    }
    const cutoff =
      selectedOutcome.result === "take_profit" || selectedOutcome.result === "stop_loss" || selectedOutcome.result === "momentum_sell"
        ? stopTakeExitTs
        : placeCheck?.check_ts ?? null;
    const trimmed = cutoff
      ? simulatedChecks.filter(
          (row) => Date.parse(row.check_ts) <= Date.parse(cutoff) && row.outcome !== "already_placed",
        )
      : simulatedChecks.filter((row) => row.outcome !== "already_placed");
    const windowStart = selectedWindowDetailQ.data?.window_start ?? simulatedChecks[0]?.check_ts ?? "";
    return collapseChecksBySecond(trimmed, windowStart);
  }, [simulatedChecks, selectedOutcome, stopTakeExitTs, placeCheck?.check_ts, selectedWindowDetailQ.data?.window_start]);

  const skipWouldBe = useMemo(() => {
    if (!selectedOutcome || selectedOutcome.result !== "skip") return null;
    const wouldDir = placeCheck?.direction ?? selectedOutcome.direction;
    if (!wouldDir || !selectedOutcome.winning_direction) return null;
    return wouldDir === selectedOutcome.winning_direction ? "win" : "loss";
  }, [selectedOutcome, placeCheck?.direction]);

  const msTrackingRows = useMemo(() => {
    if (!showWindowAnalytics || !placeCheck) return [] as SimTraceRow[];
    if (configInputs["momentum_sell_enabled"] !== "true") return [] as SimTraceRow[];
    const fillTs = Date.parse(placeCheck.check_ts);
    return simulatedChecks.filter((row) => {
      const ts = Date.parse(row.check_ts);
      if (ts <= fillTs) return false;
      return row.checks.some((c) => c.name === "momentum_sell");
    });
  }, [simulatedChecks, placeCheck, configInputs, showWindowAnalytics]);

  const msTickAskMap = useMemo(() => {
    const map = new Map<string, number>();
    const dir = selectedOutcome?.direction;
    if (!dir || !selectedTicksQ.data?.items) return map;
    for (const tick of selectedTicksQ.data.items) {
      const askRaw = dir === "UP" ? tick.up_ask : tick.down_ask;
      if (askRaw != null) {
        map.set(tick.ts, Math.ceil(Number(askRaw) * 100) / 100);
      }
    }
    return map;
  }, [selectedTicksQ.data?.items, selectedOutcome?.direction]);

  const selectedTicks = selectedTicksQ.data?.items ?? [];
  // V3 multi-market: bucket the per-second tick stream by the active
  // market's `bucket_seconds` (60s on 5m/15m, 300s on 1h, 600s on 4h)
  // and produce a tab per bucket. V2 hard-coded 5×60s buckets, which
  // truncated 15m/1h/4h windows to the first 5 minutes only.
  const simPriceFeedBucketIndices = useMemo<number[]>(
    () => Array.from({ length: viewBucketCount }, (_, i) => i + 1),
    [viewBucketCount],
  );
  const simPriceFeedBySecond = useMemo(() => {
    const map = new Map<number, Map<number, WindowTick>>();
    if (selectedTicks.length === 0 || !selectedWindowStart) return map;
    const sorted = selectedTicks
      .slice()
      .sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts));
    for (const tick of sorted) {
      const elapsed = checkSecondOffset(tick.ts, selectedWindowStart, marketWindowSeconds);
      if (elapsed == null) continue;
      const minute = Math.floor(elapsed / viewBucketSeconds) + 1;
      const secondInBucket = elapsed % viewBucketSeconds;
      if (!map.has(minute)) map.set(minute, new Map<number, WindowTick>());
      map.get(minute)!.set(secondInBucket, tick);
    }
    return map;
  }, [selectedTicks, selectedWindowStart, viewBucketSeconds, marketWindowSeconds]);
  const simPriceFeedCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const n of simPriceFeedBucketIndices) counts[n] = simPriceFeedBySecond.get(n)?.size ?? 0;
    return counts;
  }, [simPriceFeedBySecond, simPriceFeedBucketIndices]);
  // V3 multi-market: stop auto-selecting the first non-empty bucket so the
  // Price feed and Condition check tables load EMPTY by default. For
  // 1h/4h markets these tables hold 600\u20131,440 rows per bucket and many
  // thousands of condition rows; auto-rendering them on every window
  // click pegged the main thread for several seconds. The user explicitly
  // picks a bucket from the dropdown before any rows are mounted.
  // V3 multi-market: derive bucket index + slice-seconds from the active
  // slice offset, which itself is sourced from either the flat Select
  // (non-lazy markets) or the hierarchical pickers (lazy markets).
  const selectedBucketIndex = useMemo<number | null>(() => {
    if (selectedSliceOffsetSec == null) return null;
    return Math.floor(selectedSliceOffsetSec / viewBucketSeconds) + 1;
  }, [selectedSliceOffsetSec, viewBucketSeconds]);
  const selectedBucketSliceSeconds = useMemo<[number, number] | null>(() => {
    if (selectedSliceOffsetSec == null) return null;
    return [selectedSliceOffsetSec, selectedSliceOffsetSec + selectedSliceLengthSec];
  }, [selectedSliceOffsetSec, selectedSliceLengthSec]);
  const selectedWindowStartMs = useMemo(() => Date.parse(selectedWindowStart), [selectedWindowStart]);
  // V3 multi-market: condition-check rows scoped to the active bucket
  // selection. We keep each row's index into the full ``visibleChecks``
  // array so the odds-history chips (``priorRows``) still walk every
  // preceding check, not just the ones in this bucket -- chips would
  // otherwise jump as the user picks different buckets.
  const bucketScopedChecks = useMemo<Array<{ row: SimTraceRow; fullIdx: number }>>(() => {
    if (!selectedBucketSliceSeconds) return [];
    if (!Number.isFinite(selectedWindowStartMs)) return [];
    const [startSec, endSec] = selectedBucketSliceSeconds;
    const startMs = selectedWindowStartMs + startSec * 1000;
    const endMs = selectedWindowStartMs + endSec * 1000;
    const out: Array<{ row: SimTraceRow; fullIdx: number }> = [];
    for (let i = 0; i < visibleChecks.length; i++) {
      const row = visibleChecks[i]!;
      const ts = Date.parse(row.check_ts);
      if (!Number.isFinite(ts)) continue;
      if (ts >= startMs && ts < endMs) out.push({ row, fullIdx: i });
    }
    return out;
  }, [visibleChecks, selectedBucketSliceSeconds, selectedWindowStartMs]);
  const sectionMap = useMemo(
    () => FEATURE_GROUPS
      .map((group) => ({
        title: group.title,
        description: group.description,
        enabledKey: group.enabledKey,
        metaKey: group.metaKey ?? group.enabledKey,
        keys: [
          ...group.keys.filter((k) => Object.prototype.hasOwnProperty.call(configInputs, k)),
          ...(group.title === "Trade Time Ranges" && Object.prototype.hasOwnProperty.call(configInputs, "trade_time_ranges_split_results")
            ? ["trade_time_ranges_split_results"]
            : []),
          ...(group.title === "Trade Days" && Object.prototype.hasOwnProperty.call(configInputs, "trade_days_split_results")
            ? ["trade_days_split_results"]
            : []),
        ],
      }))
      .filter((group) => (group.enabledKey && Object.prototype.hasOwnProperty.call(configInputs, group.enabledKey)) || group.keys.length > 0),
    [configInputs],
  );

  function updateFeatureToggle(featureTitle: string, key: string, checked: boolean) {
    setConfigInputs((prev) => ({ ...prev, [key]: checked ? "true" : "false" }));
    if (checked) {
      expandPanel(featureTitle);
    } else {
      // Keep the card on the page while it is merely switched off (only
      // the explicit Remove action hides it), but fold it up so the
      // disabled settings stop taking space.
      collapsePanel(featureTitle);
      setAddedPanels((prev) => (prev.includes(featureTitle) ? prev : [...prev, featureTitle]));
    }
  }

  const visibleSections = useMemo(
    () => sectionMap.filter((s) => isSectionActive(s, configInputs) || addedPanels.includes(s.title)),
    [sectionMap, configInputs, addedPanels],
  );
  const availableSections = useMemo(() => {
    const shown = new Set(visibleSections.map((s) => s.title));
    return sectionMap.filter((s) => !shown.has(s.title));
  }, [sectionMap, visibleSections]);
  const orderedSections = useSortedSections(visibleSections);

  function addSection(section: FeatureSectionLike) {
    setAddedPanels((prev) => (prev.includes(section.title) ? prev : [...prev, section.title]));
    if (section.enabledKey) {
      setConfigInputs((prev) => ({ ...prev, [section.enabledKey!]: "true" }));
    }
    expandPanel(section.title);
  }

  function removeSection(section: FeatureSectionLike) {
    setAddedPanels((prev) => prev.filter((t) => t !== section.title));
    const field = valueFieldFor(section);
    setConfigInputs((prev) => {
      const next = { ...prev };
      if (section.enabledKey) next[section.enabledKey] = "false";
      else if (field && Object.prototype.hasOwnProperty.call(next, field)) next[field] = "0";
      return next;
    });
  }

  // Value-driven features (no *_enabled field) get a UI toggle that drives
  // the value: off -> 0 (remembering the previous value), on -> the
  // remembered value or a sensible default.
  function toggleValueFeature(section: FeatureSectionLike, checked: boolean) {
    const field = valueFieldFor(section);
    if (!field) return;
    if (checked) {
      const restore = stashedValues[field] ?? VALUE_FEATURE_DEFAULTS[field] ?? "1";
      setConfigInputs((prev) => ({ ...prev, [field]: restore }));
      expandPanel(section.title);
    } else {
      setStashedValues((prev) => ({ ...prev, [field]: configInputs[field] ?? prev[field] ?? VALUE_FEATURE_DEFAULTS[field] ?? "1" }));
      setConfigInputs((prev) => ({ ...prev, [field]: "0" }));
      collapsePanel(section.title);
      setAddedPanels((prev) => (prev.includes(section.title) ? prev : [...prev, section.title]));
    }
  }

  return (
    <Stack gap="lg">
      <Stack gap={2}>
        <Title order={2}>Manual Simulator</Title>
        <Text c="dimmed" size="sm">
          Pick a config, tweak strategy fields, run a single simulation, and review where orders would have landed.
        </Text>
      </Stack>

      <style>{`
        .manual-sim-form .mantine-InputWrapper-label,
        .manual-sim-form .mantine-Switch-label {
          font-size: 1rem;
          font-weight: 650;
          margin-bottom: 6px;
        }

        .manual-sim-form .mantine-InputWrapper-description,
        .manual-sim-form .mantine-Switch-description {
          font-size: 0.9rem;
        }

        .manual-sim-form .feature-title {
          margin-bottom: 4px;
        }

        .manual-sim-form .feature-description {
          margin-bottom: 10px;
        }
      `}</style>

      <Grid className="manual-sim-form" gutter="lg" maw="100%" w="100%" mx="auto" align="start">
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="md">
          <FeatureColumnToolbar
            available={availableSections}
            onAdd={addSection}
            onCollapseAll={() => setAllCollapsed(visibleSections.map((s) => s.title), true)}
            onExpandAll={() => setAllCollapsed(visibleSections.map((s) => s.title), false)}
          />

          {orderedSections.map(({ section, groupHeader }) => (
            <Fragment key={section.title}>
            {groupHeader ? <FeatureGroupHeader label={groupHeader} /> : null}
            <FeatureCardShell
              section={section}
              collapsed={collapsedPanels.includes(section.title)}
              onToggleCollapsed={() => toggleCollapsed(section.title)}
              onRemove={isSectionRemovable(section) ? () => removeSection(section) : undefined}
              enabledControl={section.enabledKey ? (
                <Switch
                  checked={configInputs[section.enabledKey] === "true"}
                  onChange={(e) => updateFeatureToggle(section.title, section.enabledKey!, e.currentTarget.checked)}
                  aria-label={`Enable ${section.title}`}
                />
              ) : valueFieldFor(section) ? (
                <Switch
                  checked={isSectionActive(section, configInputs)}
                  onChange={(e) => toggleValueFeature(section, e.currentTarget.checked)}
                  aria-label={`Enable ${section.title}`}
                />
              ) : null}
            >
                  <Stack gap="md">
                    {section.metaKey === "core" ? (
                      <>
                        <Select
                          searchable
                          label="Config"
                          description="Select base config"
                          placeholder="Select config"
                          data={versionOptions}
                          value={selectedConfigVersionId}
                          onChange={(v) => {
                            urlImportedConfigIdRef.current = null;
                            setSelectedConfigVersionId(v);
                          }}
                          disabled={versionsQ.isLoading}
                        />
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                          <TextInput
                            label="From"
                            type="datetime-local"
                            value={from}
                            onChange={(e) => setFrom(e.currentTarget.value)}
                          />
                          <TextInput
                            label="To"
                            type="datetime-local"
                            value={to}
                            onChange={(e) => setTo(e.currentTarget.value)}
                          />
                        </SimpleGrid>
                        <Switch
                          label="Subtract Historical No-Fills From Results"
                          description="Exclude historically no-fill windows from aggregate wins, losses, win rate, and P/L while still reporting no-fill counts."
                          checked={subtractNoFillsFromResults}
                          onChange={(e) => setSubtractNoFillsFromResults(e.currentTarget.checked)}
                        />
                        {/* exclude_stale_odds: drop windows whose Polymarket odds never moved */}
                        <Switch
                          label="Exclude stale windows (odds never moved)"
                          description="Drop windows whose Polymarket odds stayed pinned all window (maintenance / market-maker-absent) so they don't skew results."
                          checked={excludeStaleOdds}
                          onChange={(e) => setExcludeStaleOdds(e.currentTarget.checked)}
                        />
                        <Text size="sm" c="dimmed">
                          Selected config ID: <Code style={monoStyle}>{selectedConfigVersionId ?? "(none)"}</Code>
                        </Text>
                        <Divider label="Core entry" labelPosition="left" />
                      </>
                    ) : null}
                    {section.title === "Kronos Forecast" ? (
                      <KronosCachePicker
                        marketId={marketId}
                        // The five signature params + decision_time_seconds are
                        // NOT editable keys, so they never land in configInputs
                        // — read them from the merged active config
                        // (configOverride) so the picker reflects the cache the
                        // market is actually running on and prefills it. Only
                        // kronos_model is editable, so prefer configInputs there.
                        currentSignature={kronosSignatureFromParams({
                          model: String(configInputs["kronos_model"] ?? configOverride?.["kronos_model"] ?? "small"),
                          lookback: Number(configOverride?.["kronos_lookback"] ?? 400),
                          timeframe: Number(configOverride?.["kronos_candle_timeframe_seconds"] ?? 60),
                          sampleCount: Number(configOverride?.["kronos_sample_count"] ?? 5),
                          temperature: Number(configOverride?.["kronos_temperature"] ?? 1),
                          topP: Number(configOverride?.["kronos_top_p"] ?? 0.9),
                          settled: (() => {
                            const raw = configInputs["kronos_settled_precompute_enabled"]
                              ?? configOverride?.["kronos_settled_precompute_enabled"];
                            return raw === true || raw === "true" || raw === 1 || raw === "1";
                          })(),
                        })}
                        currentDecisionTime={Number(configInputs["decision_time_seconds"] ?? configOverride?.["decision_time_seconds"]) || null}
                        filterByDecisionTime
                        onApply={(info) =>
                          setConfigInputs((prev) => ({
                            ...prev,
                            kronos_model: info.model,
                            kronos_lookback: String(info.lookback),
                            kronos_candle_timeframe_seconds: String(info.timeframe),
                            kronos_sample_count: String(info.sample_count),
                            kronos_temperature: String(info.temperature),
                            kronos_top_p: String(info.top_p),
                            // Adopt the cache's mode so the sim reads the SAME
                            // cache (settled vs decide-late).
                            kronos_settled_precompute_enabled: info.settled ? "true" : "false",
                            decision_time_seconds: String(info.decision_time_seconds),
                          }))
                        }
                      />
                    ) : null}
                    {section.keys.length > 0 ? <Divider /> : null}
                    <Stack gap="md">
                      {section.keys.map((key) => {
                        if (key === "trade_time_ranges_split_results") {
                          return (
                            <Switch
                              key={key}
                              label="Split Results By Time Range"
                              description="Run each configured trade time range as an isolated simulation group in the result summary."
                              checked={configInputs[key] === "true"}
                              onChange={(e) => setConfigInputs((prev) => ({ ...prev, [key]: e.currentTarget.checked ? "true" : "false" }))}
                            />
                          );
                        }
                        if (key === "trade_days_split_results") {
                          return (
                            <Switch
                              key={key}
                              label="Split Results By Day"
                              description="Break the result summary down per configured weekday in an isolated group table."
                              checked={configInputs[key] === "true"}
                              onChange={(e) => setConfigInputs((prev) => ({ ...prev, [key]: e.currentTarget.checked ? "true" : "false" }))}
                            />
                          );
                        }
                        if (key === "candle_timeframe_seconds") {
                          const tfOpts = candleTimeframeOptionsFor(marketWindowSeconds);
                          return (
                            <Select
                              key={key}
                              label={formatFieldLabel(key)}
                              description={FIELD_DESCRIPTIONS[key]}
                              value={configInputs[key] ?? "0"}
                              onChange={(v) =>
                                setConfigInputs((prev) => {
                                  const nextTf = Number(v ?? "0");
                                  const nextEffective = simEffectiveTimeframeSeconds(nextTf, marketBucketSeconds);
                                  const nextTotal = Math.max(1, Math.floor(marketWindowSeconds / Math.max(1, nextEffective)));
                                  // Re-clamp dependent fields so the new
                                  // timeframe doesn't leave the form in
                                  // an invalid state.
                                  const curSkip = Number(prev["skip_first_candles"] ?? "0") || 0;
                                  const nextSkip = Math.min(curSkip, Math.max(0, nextTotal - 1));
                                  const nextRemaining = Math.max(1, nextTotal - nextSkip);
                                  const curCheck = Number(prev["candles_to_check"] ?? "1") || 1;
                                  const nextCheck = Math.min(Math.max(1, curCheck), nextRemaining);
                                  const curMin = Number(prev["min_matching_candles"] ?? "0") || 0;
                                  const nextMin = Math.min(curMin, nextCheck);
                                  const curLive = Number(prev["live_candle_minute"] ?? "0") || 0;
                                  const nextLive = curLive > nextTotal || (curLive > 0 && curLive <= nextSkip) ? 0 : curLive;
                                  return {
                                    ...prev,
                                    [key]: String(nextTf),
                                    skip_first_candles: String(nextSkip),
                                    candles_to_check: String(nextCheck),
                                    min_matching_candles: String(nextMin),
                                    live_candle_minute: String(nextLive),
                                  };
                                })
                              }
                              data={[
                                { value: "0", label: `Default (${timeframeLabel(marketBucketSeconds)})` },
                                ...tfOpts.map((o) => ({ value: String(o.seconds), label: o.label })),
                              ]}
                              allowDeselect={false}
                            />
                          );
                        }
                        if (key === "skip_first_candles") {
                          const tfRaw = Number(configInputs["candle_timeframe_seconds"] ?? "0") || 0;
                          const tfEff = simEffectiveTimeframeSeconds(tfRaw, marketBucketSeconds);
                          const totalCandles = Math.max(1, Math.floor(marketWindowSeconds / Math.max(1, tfEff)));
                          return (
                            <TextInput
                              key={key}
                              label={formatFieldLabel(key)}
                              description={`${FIELD_DESCRIPTIONS[key]} Window has ${totalCandles} total ${timeframeLabel(tfEff)} candles (0..${totalCandles - 1}).`}
                              value={configInputs[key] ?? "0"}
                              onChange={(e) =>
                                setConfigInputs((prev) => {
                                  const nextSkip = Math.max(0, Math.min(totalCandles - 1, Math.floor(Number(e.currentTarget.value) || 0)));
                                  const nextRemaining = Math.max(1, totalCandles - nextSkip);
                                  const curCheck = Number(prev["candles_to_check"] ?? "1") || 1;
                                  const nextCheck = Math.min(Math.max(1, curCheck), nextRemaining);
                                  const curMin = Number(prev["min_matching_candles"] ?? "0") || 0;
                                  const nextMin = Math.min(curMin, nextCheck);
                                  const curLive = Number(prev["live_candle_minute"] ?? "0") || 0;
                                  const nextLive = curLive > 0 && curLive <= nextSkip ? 0 : curLive;
                                  return {
                                    ...prev,
                                    [key]: String(nextSkip),
                                    candles_to_check: String(nextCheck),
                                    min_matching_candles: String(nextMin),
                                    live_candle_minute: String(nextLive),
                                  };
                                })
                              }
                              styles={{ input: monoStyle }}
                            />
                          );
                        }
                        if (key === "live_candle_minute") {
                          if (configInputs["allow_live_candle"] !== "true") return null;
                          // V3 multi-market: dropdown options scale with
                          // the timeframe + skip-first count so the
                          // operator can only pick a still-eligible
                          // candle index.
                          const tfRaw = Number(configInputs["candle_timeframe_seconds"] ?? "0") || 0;
                          const tfEff = simEffectiveTimeframeSeconds(tfRaw, marketBucketSeconds);
                          const totalCandles = Math.max(1, Math.floor(marketWindowSeconds / Math.max(1, tfEff)));
                          const skip = Math.max(0, Math.min(totalCandles - 1, Number(configInputs["skip_first_candles"] ?? "0") || 0));
                          const remaining = Math.max(1, totalCandles - skip);
                          const data: Array<{ value: string; label: string }> = [
                            { value: "0", label: `Any candle (${remaining} eligible)` },
                          ];
                          for (let i = skip + 1; i <= totalCandles; i += 1) {
                            data.push({ value: String(i), label: `Candle ${i}` });
                          }
                          // A loaded result may carry a live_candle_minute that
                          // is out of range for the *currently active* market's
                          // window/timeframe (e.g. 7 on a window that only has 6
                          // candles when the result was generated on a larger
                          // window). Without an option for it, the Select renders
                          // empty and silently loses the saved value. Surface it
                          // as an explicit out-of-range option so re-runs replay
                          // faithfully and the operator can see it needs fixing.
                          const curVal = Number(configInputs[key] ?? "0") || 0;
                          if (curVal > 0 && !data.some((o) => o.value === String(curVal))) {
                            data.push({ value: String(curVal), label: `Candle ${curVal} (out of range)` });
                          }
                          return (
                            <Select
                              key={key}
                              label={formatFieldLabel(key)}
                              description={`Earliest candle index on which the live-candle bonus may fire. Defaults to 'Any candle' which enables on every eligible candle (after skip).`}
                              value={configInputs[key] ?? "0"}
                              onChange={(v) => setConfigInputs((prev) => ({ ...prev, [key]: v ?? "0" }))}
                              data={data}
                              allowDeselect={false}
                            />
                          );
                        }
                        if (key === "momentum_sell_window_type") {
                          return (
                            <Select
                              key={key}
                              label={formatFieldLabel(key)}
                              description={FIELD_DESCRIPTIONS[key]}
                              value={configInputs[key] ?? "number"}
                              onChange={(v) => setConfigInputs((prev) => ({ ...prev, [key]: v ?? "number" }))}
                              data={[
                                { value: "number", label: "Fixed seconds" },
                                { value: "percentage", label: "% of remaining window at fill" },
                              ]}
                              allowDeselect={false}
                            />
                          );
                        }
                        if (key === "momentum_sell_sl_threshold_type" || key === "momentum_sell_tp_threshold_type") {
                          const isSl = key === "momentum_sell_sl_threshold_type";
                          return (
                            <Select
                              key={key}
                              label={formatFieldLabel(key)}
                              description={FIELD_DESCRIPTIONS[key]}
                              value={configInputs[key] ?? "points"}
                              onChange={(v) => setConfigInputs((prev) => ({ ...prev, [key]: v ?? "points" }))}
                              data={[
                                { value: "points", label: isSl ? "Odds points below fill" : "Odds points above fill" },
                                { value: "percentage", label: isSl ? "% below fill ask" : "% above fill ask" },
                              ]}
                              allowDeselect={false}
                            />
                          );
                        }
                        if (key === "counterpart_confirm_mode") {
                          return (
                            <Select
                              key={key}
                              label={formatFieldLabel(key)}
                              description={FIELD_DESCRIPTIONS[key]}
                              value={configInputs[key] ?? "price"}
                              onChange={(v) => setConfigInputs((prev) => ({ ...prev, [key]: v ?? "price" }))}
                              data={[
                                { value: "price", label: "Counterpart underlying move vs its open" },
                                { value: "favorite", label: "Counterpart favourite side must match" },
                              ]}
                              allowDeselect={false}
                            />
                          );
                        }
                        if (key === "kronos_model") {
                          return (
                            <Select
                              key={key}
                              label={formatFieldLabel(key)}
                              description={FIELD_DESCRIPTIONS[key]}
                              value={configInputs[key] ?? "small"}
                              onChange={(v) => setConfigInputs((prev) => ({ ...prev, [key]: v ?? "small" }))}
                              data={KRONOS_MODEL_OPTIONS}
                              allowDeselect={false}
                            />
                          );
                        }
                        if (key === "counterpart_decide_within_pct") {
                          // Translate the % into the concrete elapsed-second
                          // window the override can actually ENTER, taking the
                          // decision time and (never-bypassed) no-buy cutoff
                          // into account.
                          const W = marketWindowSeconds;
                          const D = Math.max(0, Number(configInputs["decision_time_seconds"]) || 0);
                          const NB = Math.max(0, Number(configInputs["no_buy_window_seconds"]) || 0);
                          const P = Math.max(0, Number(configInputs[key]) || 0);
                          const maxSecsLeft = P > 0 ? Math.min(W - D, Math.floor((P / 100) * W)) : (W - D);
                          const startElapsed = W - maxSecsLeft;
                          const endElapsed = NB > 0 ? W - NB - 1 : W - 1;
                          return (
                            <Stack key={key} gap={4}>
                              <TextInput
                                label={formatFieldLabel(key)}
                                description={FIELD_DESCRIPTIONS[key]}
                                value={configInputs[key] ?? ""}
                                onChange={(e) => setConfigInputs((prev) => ({ ...prev, [key]: e.currentTarget.value }))}
                                styles={{ input: monoStyle }}
                              />
                              {maxSecsLeft < 0
                                ? <Text size="xs" c="orange">Decision time ({D}s) is at/after window close ({W}s) — the override never fires.</Text>
                                : startElapsed > endElapsed
                                  ? <Text size="xs" c="orange">At {P}% the earliest entry is {startElapsed}s, but the {NB}s no-buy cutoff blocks everything after {endElapsed}s — the override can never place. Raise the % or lower the no-buy window.</Text>
                                  : <Text size="xs" c="dimmed">Effective entry window: <b>{startElapsed}s → {endElapsed}s</b> into the {W}s window (decision time {D}s, no-buy {NB}s).</Text>}
                            </Stack>
                          );
                        }
                        const templateValue = configTemplate?.[key];
                        if (typeof templateValue === "boolean") {
                          return (
                            <Switch
                              key={key}
                              label={formatFieldLabel(key)}
                              description={FIELD_DESCRIPTIONS[key]}
                              checked={configInputs[key] === "true"}
                              onChange={(e) => setConfigInputs((prev) => ({ ...prev, [key]: e.currentTarget.checked ? "true" : "false" }))}
                            />
                          );
                        }
                        return (
                          <TextInput
                            key={key}
                            label={formatFieldLabel(key)}
                            description={FIELD_DESCRIPTIONS[key]}
                            value={configInputs[key] ?? ""}
                            onChange={(e) => setConfigInputs((prev) => ({ ...prev, [key]: e.currentTarget.value }))}
                            styles={{ input: monoStyle }}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
            </FeatureCardShell>
            </Fragment>
          ))}

          <Card withBorder>
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div>
                  <Text className="feature-title" size="lg" fw={700}>Show Result List</Text>
                  <Text className="feature-description" size="sm" c="dimmed">
                    Toggle simulated result windows list and optional deep per-window analytics.
                  </Text>
                </div>
                <Switch
                  mt={2}
                  checked={showResultList}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    setShowResultList(checked);
                    if (!checked) setDisplaySimulatedWindowAnalytics(false);
                  }}
                />
              </Group>

              <Collapse in={showResultList}>
                <Stack gap="md">
                  <Divider />
                  <Switch
                    label="Display Simulated Window Analytics"
                    checked={displaySimulatedWindowAnalytics}
                    onChange={(e) => setDisplaySimulatedWindowAnalytics(e.currentTarget.checked)}
                  />
                </Stack>
              </Collapse>
            </Stack>
          </Card>

          <Group justify="space-between" align="center">
            <Button
              variant="default"
              onClick={() => {
                if (!selectedVersion) return;
                const cfg = augmentConfigWithDefaults((selectedVersion.config ?? {}) as Record<string, unknown>);
                setConfigTemplate(cfg);
                setConfigInputs(
                  {
                    ...Object.fromEntries(
                      Object.entries(cfg)
                        .filter(([k]) => !HIDDEN_FIELDS.has(k) && EDITABLE_KEYS.has(k))
                        .map(([k, v]) => [k, valueToInput(v)]),
                    ),
                    trade_time_ranges_split_results: "false",
                    trade_days_split_results: "false",
                  },
                );
                setAddedPanels([]);
                setShowResultList(false);
                setDisplaySimulatedWindowAnalytics(false);
              }}
              disabled={!selectedVersion}
            >
              Reset Changes
            </Button>
            <Button
              onClick={() => runMut.mutate()}
              loading={runMut.isPending}
              disabled={!configOverride || versionsQ.isLoading}
            >
              Run Manual Simulation
            </Button>
          </Group>
        </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Card withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Title order={4}>Simulation Result</Title>
                <Group gap="xs">
                  {runQ.data ? <Badge color={statusColor(runQ.data.status)}>{runQ.data.status}</Badge> : null}
                  {runQ.data && !FINAL_STATUSES.has(runQ.data.status) ? (
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      leftSection={<IconPlayerStop size={14} />}
                      loading={cancelMut.isPending}
                      onClick={() => cancelMut.mutate()}
                    >
                      Cancel
                    </Button>
                  ) : null}
                  {configOverride ? (
                    <Button
                      size="xs"
                      color="grape"
                      variant="light"
                      leftSection={<IconDownload size={14} />}
                      onClick={() => downloadConfigJson(configOverride, configTemplate)}
                    >
                      Save Config as JSON
                    </Button>
                  ) : null}
                  {runQ.data && FINAL_STATUSES.has(runQ.data.status) ? (
                    runQ.data.saved ? (
                      <Button
                        size="xs"
                        color="gray"
                        variant="light"
                        leftSection={<IconCheck size={14} />}
                        onClick={() => saveMut.mutate(false)}
                        loading={saveMut.isPending}
                      >
                        Saved · Unsave
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        color="blue"
                        variant="light"
                        leftSection={<IconDeviceFloppy size={14} />}
                        onClick={() => saveMut.mutate(true)}
                        loading={saveMut.isPending}
                      >
                        Save Results
                      </Button>
                    )
                  ) : null}
                  {runQ.data && FINAL_STATUSES.has(runQ.data.status) ? (
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      leftSection={<IconX size={14} />}
                      onClick={() => {
                        if (window.confirm("Delete this run? Its results will be permanently removed.")) {
                          discardMut.mutate();
                        }
                      }}
                      loading={discardMut.isPending}
                    >
                      Delete
                    </Button>
                  ) : null}
                </Group>
              </Group>

              {!runId ? <EmptyState label="No run started yet." /> : null}
              {runQ.isLoading ? <LoadingState label="Polling simulation run..." /> : null}
              {runQ.isError ? <ErrorState error={runQ.error as Error} onRetry={() => runQ.refetch()} /> : null}

              {runQ.data ? (
                <Stack gap="sm">
                  <Text size="sm">Run ID: <Code style={monoStyle}>{runQ.data.run_id}</Code></Text>
                  <Badge color={noFillSubtractionEnabled ? "teal" : "gray"} variant="light">
                    No-Fill Subtraction: {noFillSubtractionEnabled ? "ON" : "OFF"}
                  </Badge>
                  <Badge color={staleOddsExcludedEnabled ? "teal" : "gray"} variant="light">
                    Stale Excluded: {staleOddsExcludedEnabled ? "ON" : "OFF"}
                  </Badge>
                  <Progress value={(runQ.data.progress ?? 0) * 100} color={statusColor(runQ.data.status)} />
                  {runQ.data.error ? <Text size="sm" c="red">{runQ.data.error}</Text> : null}

                  {summary ? (
                    <>
                      <SimpleGrid cols={{ base: 2, md: 4 }}>
                        <Stat label="Windows" value={String(summary.total_windows)} />
                        <Stat label="Orders" value={String(summary.windows_with_bet)} />
                        <Stat label="Wins / Losses" value={`${summary.wins} / ${summary.losses_including_stop_losses}`} />
                        <Stat label="Win Rate" value={`${(Number(summary.win_rate || 0) * 100).toFixed(1)}%`} />
                        <Stat label="Total Winnings" value={`$${Number(summary.total_won_usdc || 0).toFixed(2)}`} accent="green" />
                        <Stat label="Total Losses" value={`$${Number(summary.total_lost_usdc || 0).toFixed(2)}`} accent="red" />
                        <Stat
                          label="Take Profits"
                          value={String(summary.take_profits)}
                          subValue={`$${Number(summary.take_profit_total_won_usdc || 0).toFixed(2)} won`}
                          accent="green"
                          subAccent="green"
                        />
                        <Stat
                          label="Stop Losses"
                          value={String(summary.stop_losses)}
                          subValue={`$${Number(summary.stop_loss_total_lost_usdc || 0).toFixed(2)} lost`}
                          accent="red"
                          subAccent="red"
                        />
                        <Stat
                          label="Momentum Profit Sells"
                          value={String(summary.momentum_sell_profit_count ?? 0)}
                          subValue={`$${Number(summary.momentum_sell_profit_pnl_usdc || 0).toFixed(2)} won`}
                          accent="green"
                          subAccent="green"
                        />
                        <Stat
                          label="Momentum Loss Sells"
                          value={String(summary.momentum_sell_loss_count ?? 0)}
                          subValue={`$${Number(summary.momentum_sell_loss_usdc || 0).toFixed(2)} lost`}
                          accent="red"
                          subAccent="red"
                        />
                        <Stat
                          label="No-Fill Wins / Losses"
                          value={`${summary.no_fill_wins} / ${summary.no_fill_losses}`}
                        />
                        <Stat label="Net P/L" value={`$${Number(summary.pnl_usdc || 0).toFixed(2)}`} accent={Number(summary.pnl_usdc || 0) >= 0 ? "green" : "red"} />
                        <Stat label="Fees" value={`$${Number(summary.fees_usdc || 0).toFixed(2)}`} />
                        <Stat label="Skipped" value={String(summary.skips)} />
                        <Stat label="No Bet" value={String(summary.no_bet)} />
                        <Stat
                          label="Counterpart Market Override W/L"
                          value={`${counterpartWL.overrideW} / ${counterpartWL.overrideL}`}
                        />
                        <Stat
                          label="Counterpart Matching Order W/L"
                          value={`${counterpartWL.matchW} / ${counterpartWL.matchL}`}
                        />
                        {/*
                          V3 multi-market: an order placed on a window whose
                          `winning_direction` is still NULL (resolver hasn't
                          fetched the outcome from Polymarket Gamma yet, or
                          the asset isn't being resolved) is counted as
                          "Unresolved" — it isn't a win, loss, or no-fill.
                          Surface it explicitly so a result like
                          "Orders=1, Wins/Losses=0/0, No-Fill=0/0" is no
                          longer a mystery.
                        */}
                        <Stat
                          label="Unresolved"
                          value={String(summary.unresolved ?? 0)}
                          subValue={
                            (summary.unresolved ?? 0) > 0
                              ? "no winning direction yet"
                              : undefined
                          }
                        />
                      </SimpleGrid>

                      {displayRangeBreakdown.length > 0 ? (
                        <Card withBorder>
                          <Stack gap="xs">
                            <Title order={5}>Trade Time Range Breakdown</Title>
                            <Table withTableBorder striped>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th style={{ width: 32 }} />
                                  <Table.Th>Range</Table.Th>
                                  <Table.Th>Wins / Trades</Table.Th>
                                  <Table.Th>Win Rate</Table.Th>
                                  <Table.Th>Won</Table.Th>
                                  <Table.Th>Lost</Table.Th>
                                  <Table.Th>Net P/L</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {displayRangeBreakdown.map((g) => {
                                  const isChecked = !uncheckedRanges.has(g.range);
                                  return (
                                    <Table.Tr key={g.range} style={{ opacity: isChecked ? 1 : 0.4 }}>
                                      <Table.Td>
                                        <Checkbox
                                          checked={isChecked}
                                          onChange={(e) => {
                                            const checked = e.currentTarget.checked;
                                            const allRanges = displayRangeBreakdown.map((r) => r.range);
                                            const next = new Set(uncheckedRanges);
                                            if (!checked) next.add(g.range);
                                            else next.delete(g.range);
                                            setUncheckedRanges(next);
                                            const newVal = allRanges
                                              .filter((r) => !next.has(r))
                                              .sort((a, b) => a.localeCompare(b))
                                              .join(",");
                                            setConfigInputs((prev) => ({ ...prev, trade_time_ranges: newVal }));
                                          }}
                                        />
                                      </Table.Td>
                                      <Table.Td>{g.range}</Table.Td>
                                      <Table.Td>{g.wins} / {g.windows_with_bet}</Table.Td>
                                      <Table.Td>{(Number(g.win_rate || 0) * 100).toFixed(1)}%</Table.Td>
                                      <Table.Td style={{ color: "var(--mantine-color-green-6)" }}>${Number(g.total_won_usdc || 0).toFixed(2)}</Table.Td>
                                      <Table.Td style={{ color: "var(--mantine-color-red-6)" }}>${Number(g.total_lost_usdc || 0).toFixed(2)}</Table.Td>
                                      <Table.Td style={{ color: Number(g.net_pnl_usdc || 0) >= 0 ? "var(--mantine-color-green-6)" : "var(--mantine-color-red-6)" }}>
                                        ${Number(g.net_pnl_usdc || 0).toFixed(2)}
                                      </Table.Td>
                                    </Table.Tr>
                                  );
                                })}
                              </Table.Tbody>
                            </Table>
                          </Stack>
                        </Card>
                      ) : null}

                      {displayDayBreakdown.length > 0 ? (
                        <Card withBorder>
                          <Stack gap="xs">
                            <Title order={5}>Trade Day Breakdown</Title>
                            <Table withTableBorder striped>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th style={{ width: 32 }} />
                                  <Table.Th>Day</Table.Th>
                                  <Table.Th>Wins / Trades</Table.Th>
                                  <Table.Th>Win Rate</Table.Th>
                                  <Table.Th>Won</Table.Th>
                                  <Table.Th>Lost</Table.Th>
                                  <Table.Th>Net P/L</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {displayDayBreakdown.map((g) => {
                                  const isChecked = !uncheckedDays.has(g.day);
                                  return (
                                    <Table.Tr key={g.day} style={{ opacity: isChecked ? 1 : 0.4 }}>
                                      <Table.Td>
                                        <Checkbox
                                          checked={isChecked}
                                          onChange={(e) => {
                                            const checked = e.currentTarget.checked;
                                            const allDays = displayDayBreakdown.map((r) => r.day);
                                            const next = new Set(uncheckedDays);
                                            if (!checked) next.add(g.day);
                                            else next.delete(g.day);
                                            setUncheckedDays(next);
                                            const newVal = allDays
                                              .filter((r) => !next.has(r))
                                              .sort((a, b) => dayLabelOrder(a) - dayLabelOrder(b))
                                              .join(",");
                                            setConfigInputs((prev) => ({ ...prev, trade_days_allowed: newVal }));
                                          }}
                                        />
                                      </Table.Td>
                                      <Table.Td>{g.day}</Table.Td>
                                      <Table.Td>{g.wins} / {g.windows_with_bet}</Table.Td>
                                      <Table.Td>{(Number(g.win_rate || 0) * 100).toFixed(1)}%</Table.Td>
                                      <Table.Td style={{ color: "var(--mantine-color-green-6)" }}>${Number(g.total_won_usdc || 0).toFixed(2)}</Table.Td>
                                      <Table.Td style={{ color: "var(--mantine-color-red-6)" }}>${Number(g.total_lost_usdc || 0).toFixed(2)}</Table.Td>
                                      <Table.Td style={{ color: Number(g.net_pnl_usdc || 0) >= 0 ? "var(--mantine-color-green-6)" : "var(--mantine-color-red-6)" }}>
                                        ${Number(g.net_pnl_usdc || 0).toFixed(2)}
                                      </Table.Td>
                                    </Table.Tr>
                                  );
                                })}
                              </Table.Tbody>
                            </Table>
                          </Stack>
                        </Card>
                      ) : null}

                      <GateImpactCard
                        key={runQ.data.run_id}
                        runId={runQ.data.run_id}
                        impact={summary.gate_impact}
                        featureTitles={FEATURE_TITLES}
                        canAblate={runQ.data.status === "succeeded"}
                      />

                      <Divider />

                      {showResultList ? (
                        <>
                          <Stack gap="xs">
                            <Group justify="space-between">
                              <Title order={5}>Windows With Simulated Outcomes</Title>
                              <Group gap="xs">
                                <Badge variant="light">{orderWindows.length} windows</Badge>
                                <Button
                                  size="xs"
                                  variant="light"
                                  leftSection={<IconDownload size={14} />}
                                  loading={exportProgress != null && exportProgress.done < exportProgress.total}
                                  disabled={!runId || orderWindows.length === 0}
                                  onClick={async () => {
                                    if (!runId) return;
                                    try {
                                      await exportSimulatedData({
                                        runId,
                                        orderWindows,
                                        summary,
                                        configOverride,
                                        subtractNoFillsFromResults: noFillSubtractionEnabled,
                                        excludeStaleOdds: staleOddsExcludedEnabled,
                                        fromInput: from,
                                        toInput: to,
                                        onProgress: setExportProgress,
                                      });
                                      notifications.show({
                                        color: "green",
                                        title: "Export complete",
                                        message: `Downloaded ${orderWindows.length} window(s) of simulated data.`,
                                      });
                                    } catch (err) {
                                      notifications.show({
                                        color: "red",
                                        title: "Export failed",
                                        message: extractErrorMessage(err),
                                      });
                                    } finally {
                                      setExportProgress(null);
                                    }
                                  }}
                                >
                                  {exportProgress && exportProgress.done < exportProgress.total
                                    ? `Exporting ${exportProgress.done}/${exportProgress.total}…`
                                    : "Export Simulated Data"}
                                </Button>
                              </Group>
                            </Group>

                            {orderWindows.length === 0 ? (
                              <EmptyState label="No simulated order windows in this run." />
                            ) : (
                              <ScrollArea h={460} type="auto" offsetScrollbars>
                                <Table striped highlightOnHover withTableBorder>
                                  <Table.Thead>
                                    <Table.Tr>
                                      <Table.Th>Order Window</Table.Th>
                                      <Table.Th>Pick</Table.Th>
                                      <Table.Th>Winner</Table.Th>
                                      <Table.Th>Odds</Table.Th>
                                      <Table.Th>Streak</Table.Th>
                                      <Table.Th>P/L</Table.Th>
                                      <Table.Th>Result</Table.Th>
                                    </Table.Tr>
                                  </Table.Thead>
                                  <Table.Tbody>
                                    {orderWindowItems.map((item) => {
                                      if (item.type === "date") {
                                        return (
                                          <Table.Tr key={item.key}>
                                            <Table.Td colSpan={7}>
                                              <Text size="sm" ta="center" c="dimmed" fw={700}>
                                                {`-------------- ${item.label} --------------`}
                                              </Text>
                                            </Table.Td>
                                          </Table.Tr>
                                        );
                                      }
                                      const row = item.row;
                                      return (
                                        <Table.Tr key={item.key}>
                                          <Table.Td>
                                            {showWindowAnalytics ? (
                                              <Anchor
                                                component="button"
                                                type="button"
                                                onClick={() => setSelectedOutcomeSlug(row.slug)}
                                                fw={selectedOutcomeSlug === row.slug ? 700 : 500}
                                              >
                                                {item.title}
                                              </Anchor>
                                            ) : (
                                              <Text fw={500}>{item.title}</Text>
                                            )}
                                          </Table.Td>
                                          <Table.Td>{row.direction ?? "—"}</Table.Td>
                                          <Table.Td>{row.winning_direction ?? "—"}</Table.Td>
                                          <Table.Td>{row.favorite_odds ?? "—"}</Table.Td>
                                          <Table.Td style={monoStyle}>{row.trade_size_usdc != null && row.trade_size_usdc > 0 ? row.win_streak_at_bet : "—"}</Table.Td>
                                          <Table.Td style={{ color: row.pnl_usdc >= 0 ? "var(--mantine-color-green-6)" : "var(--mantine-color-red-6)" }}>
                                            {row.pnl_usdc >= 0 ? "+" : ""}{row.pnl_usdc.toFixed(2)}
                                          </Table.Td>
                                          <Table.Td>
                                            <Group gap={6} wrap="nowrap">
                                              <Badge color={resultColor(row.result)}>{row.result.toUpperCase()}</Badge>
                                              {orderUsedCounterpartOverride(row) ? (
                                                <Tooltip label="Entered via Counterpart Market Override" withArrow>
                                                  <IconRefresh
                                                    size={15}
                                                    color="var(--mantine-color-orange-5)"
                                                    aria-label="Counterpart Market Override"
                                                  />
                                                </Tooltip>
                                              ) : null}
                                              {orderUsedCounterpartMatchOrder(row) ? (
                                                <Tooltip label="Entered via Counterpart Matching Order" withArrow>
                                                  <IconCashBanknoteMoveBack
                                                    size={15}
                                                    color="var(--mantine-color-orange-5)"
                                                    aria-label="Counterpart Matching Order"
                                                  />
                                                </Tooltip>
                                              ) : null}
                                            </Group>
                                          </Table.Td>
                                        </Table.Tr>
                                      );
                                    })}
                                  </Table.Tbody>
                                </Table>
                              </ScrollArea>
                            )}
                          </Stack>

                          {showWindowAnalytics && selectedOutcome ? (
                            <>
                              <Divider my="sm" />
                              <Stack gap="md">
                                <Title order={5}>
                                  Simulated Market Data: {windowStartFromSlug(selectedOutcome.slug)
                                    ? formatWindowTitle(windowStartFromSlug(selectedOutcome.slug)!, marketWindowSeconds)
                                    : selectedOutcome.slug}
                                </Title>

                                {/*
                                  V3 multi-market: only the window-detail query is allowed to block
                                  the whole detail view. The ticks + outcome-trace queries can each
                                  return 10k\u2013100k rows on a 4-hour window (1Hz/ms ticks +
                                  per-second condition trace); waiting for them before rendering
                                  the Market / Bet summary cards made every window click feel
                                  frozen. Their loading state is surfaced inline inside the cards
                                  that actually consume them.
                                */}
                                {selectedWindowDetailQ.isLoading ? (
                                  <LoadingState label="Loading selected window market data..." />
                                ) : null}
                                {selectedWindowDetailQ.isError ? (
                                  <ErrorState error={selectedWindowDetailQ.error as Error} onRetry={() => selectedWindowDetailQ.refetch()} />
                                ) : null}
                                {selectedTicksQ.isError ? (
                                  <ErrorState error={selectedTicksQ.error as Error} onRetry={() => selectedTicksQ.refetch()} />
                                ) : null}

                                {selectedWindowDetailQ.data ? (
                                  <>
                                    <Card withBorder>
                                      <Stack gap="xs">
                                        <Title order={5}>Market</Title>
                                        <Table withTableBorder={false} style={monoStyle} fz="sm">
                                          <Table.Tbody>
                                            <Table.Tr><Table.Td>Window Start</Table.Td><Table.Td>{new Date(selectedWindowDetailQ.data.window_start).toLocaleString()}</Table.Td></Table.Tr>
                                            <Table.Tr><Table.Td>Window End</Table.Td><Table.Td>{new Date(selectedWindowDetailQ.data.window_end).toLocaleString()}</Table.Td></Table.Tr>
                                            <Table.Tr><Table.Td>BTC Open</Table.Td><Table.Td>{selectedWindowDetailQ.data.btc_open ?? "—"}</Table.Td></Table.Tr>
                                            <Table.Tr><Table.Td>BTC Close</Table.Td><Table.Td>{selectedWindowDetailQ.data.btc_close ?? "—"}</Table.Td></Table.Tr>
                                            <Table.Tr><Table.Td>BTC Delta</Table.Td><Table.Td>{selectedWindowDetailQ.data.btc_delta ?? "—"}</Table.Td></Table.Tr>
                                            <Table.Tr><Table.Td>Winning Direction</Table.Td><Table.Td>{selectedOutcome.winning_direction ?? "—"}</Table.Td></Table.Tr>
                                          </Table.Tbody>
                                        </Table>
                                      </Stack>
                                    </Card>

                                    <Card withBorder>
                                      <Group justify="space-between" mb="xs">
                                        <Title order={5}>Bet (Simulated)</Title>
                                        <Group gap="xs">
                                          {selectedOutcome.result === "skip" && skipWouldBe ? (
                                            <Badge color={skipWouldBe === "win" ? "green" : "red"}>
                                              WOULD BE {skipWouldBe.toUpperCase()}
                                            </Badge>
                                          ) : null}
                                          <Badge color={resultColor(selectedOutcome.result)}>
                                            {selectedOutcome.result.toUpperCase()}
                                          </Badge>
                                        </Group>
                                      </Group>
                                      <Table withTableBorder={false} style={monoStyle} fz="sm">
                                        <Table.Tbody>
                                          <Table.Tr><Table.Td>Direction</Table.Td><Table.Td>{selectedOutcome.direction ?? placeCheck?.direction ?? "—"}</Table.Td></Table.Tr>
                                          <Table.Tr><Table.Td>Status (raw)</Table.Td><Table.Td>{placeCheck && selectedOutcome.decision_outcome === "already_placed" ? "place" : (selectedOutcome.decision_outcome || "—")}</Table.Td></Table.Tr>
                                          <Table.Tr><Table.Td>Size (USDC)</Table.Td><Table.Td>{selectedOutcome.trade_size_usdc != null && selectedOutcome.trade_size_usdc > 0 ? selectedOutcome.trade_size_usdc.toFixed(4) : String(configOverride?.trade_size_usdc ?? "—")}</Table.Td></Table.Tr>
                                          <Table.Tr><Table.Td>Win Streak (at bet)</Table.Td><Table.Td>{selectedOutcome.trade_size_usdc != null && selectedOutcome.trade_size_usdc > 0 ? selectedOutcome.win_streak_at_bet : "—"}</Table.Td></Table.Tr>
                                          <Table.Tr><Table.Td>Price</Table.Td><Table.Td>{selectedOutcome.favorite_odds ?? placeCheck?.favorite_odds ?? "—"}</Table.Td></Table.Tr>
                                          <Table.Tr><Table.Td>Net P/L</Table.Td><Table.Td>{selectedOutcome.pnl_usdc >= 0 ? "+" : ""}{selectedOutcome.pnl_usdc.toFixed(2)}</Table.Td></Table.Tr>
                                          <Table.Tr><Table.Td>Bought at</Table.Td><Table.Td>{placeCheck ? new Date(placeCheck.check_ts).toLocaleString() : "—"}</Table.Td></Table.Tr>
                                          <Table.Tr><Table.Td>Exit at</Table.Td><Table.Td>{stopTakeExitTs ? new Date(stopTakeExitTs).toLocaleString() : "—"}</Table.Td></Table.Tr>
                                          {selectedOutcome.result === "skip" ? (
                                            <Table.Tr><Table.Td>Simulated Action</Table.Td><Table.Td>This would be skipped.</Table.Td></Table.Tr>
                                          ) : null}
                                        </Table.Tbody>
                                      </Table>
                                    </Card>

                                    <Card withBorder>
                                      <Stack gap="xs">
                                        <Stack gap={6}>
                                          <Title order={5}>Price feed (1s)</Title>
                                          {/*
                                            V3 multi-market: bucket selector drives BOTH this card and
                                            the Condition check card below. Hierarchical pill rows let
                                            the user drill Hour > 15-min > 5-min > 1-min. Each tier is
                                            independent so switching a higher tier keeps the lower
                                            selections intact (when still valid). Tiers are added
                                            top-down based on what fits inside ``window_seconds``:
                                              - 5m  (300s)   :                              1-min only
                                              - 15m (900s)   :                     5-min  > 1-min
                                              - 1h  (3600s)  :         15-min   >  5-min  > 1-min
                                              - 4h  (14400s) : Hour  > 15-min   >  5-min  > 1-min
                                            For lazy markets (1h / 4h) the ticks API is only hit once
                                            the (hour, quarter, five-min) triple is complete, so a
                                            single drill-down loads ~300 rows instead of 10k+.
                                          */}
                                          <Stack gap={4}>
                                              {showHourPicker ? (
                                                <Group gap="xs" align="center" wrap="wrap">
                                                  <Text size="xs" c="dimmed" w={56}>Hour</Text>
                                                  <SegmentedControl
                                                    size="xs"
                                                    value={lazyHour == null ? "" : String(lazyHour)}
                                                    onChange={(v) => setLazyHour(v === "" ? null : Number(v))}
                                                    data={Array.from({ length: hourOptionCount }, (_, i) => {
                                                      const offsetSec = i * 3600;
                                                      const label = Number.isFinite(selectedWindowStartMs)
                                                        ? new Date(selectedWindowStartMs + offsetSec * 1000)
                                                            .toLocaleTimeString([], { hour: "numeric", hour12: true })
                                                        : `H${i + 1}`;
                                                      return { value: String(i), label };
                                                    })}
                                                  />
                                                </Group>
                                              ) : null}
                                              {showQuarterPicker ? (
                                                <Group gap="xs" align="center" wrap="wrap">
                                                  <Text size="xs" c="dimmed" w={56}>15-min</Text>
                                                  <SegmentedControl
                                                    size="xs"
                                                    value={lazyQuarter == null ? "" : String(lazyQuarter)}
                                                    onChange={(v) => setLazyQuarter(v === "" ? null : Number(v))}
                                                    disabled={showHourPicker && lazyHour == null}
                                                    data={Array.from({ length: quartersInActiveHour }, (_, j) => {
                                                      const baseHourSec = showHourPicker ? (lazyHour ?? 0) * 3600 : 0;
                                                      const offsetSec = baseHourSec + j * 900;
                                                      const fmt = (s: number): string => Number.isFinite(selectedWindowStartMs)
                                                        ? new Date(selectedWindowStartMs + s * 1000)
                                                            .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                                        : `+${s}s`;
                                                      return { value: String(j), label: `${fmt(offsetSec)}\u2013${fmt(offsetSec + 900)}` };
                                                    })}
                                                  />
                                                </Group>
                                              ) : null}
                                              {showFiveMinPicker ? (
                                                <Group gap="xs" align="center" wrap="wrap">
                                                  <Text size="xs" c="dimmed" w={56}>5-min</Text>
                                                  <SegmentedControl
                                                    size="xs"
                                                    value={lazyFiveMin == null ? "" : String(lazyFiveMin)}
                                                    onChange={(v) => setLazyFiveMin(v === "" ? null : Number(v))}
                                                    disabled={(showHourPicker && lazyHour == null) || (showQuarterPicker && lazyQuarter == null)}
                                                    data={Array.from({ length: fiveMinsInActiveQuarter }, (_, k) => {
                                                      const baseHourSec = showHourPicker ? (lazyHour ?? 0) * 3600 : 0;
                                                      const baseQuarterSec = showQuarterPicker ? (lazyQuarter ?? 0) * 900 : 0;
                                                      const offsetSec = baseHourSec + baseQuarterSec + k * 300;
                                                      const fmt = (s: number): string => Number.isFinite(selectedWindowStartMs)
                                                        ? new Date(selectedWindowStartMs + s * 1000)
                                                            .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                                        : `+${s}s`;
                                                      return { value: String(k), label: `${fmt(offsetSec)}\u2013${fmt(offsetSec + 300)}` };
                                                    })}
                                                  />
                                                </Group>
                                              ) : null}
                                              {showOneMinPicker ? (
                                                <Group gap="xs" align="center" wrap="wrap">
                                                  <Text size="xs" c="dimmed" w={56}>1-min</Text>
                                                  <SegmentedControl
                                                    size="xs"
                                                    value={lazyOneMin == null ? "" : String(lazyOneMin)}
                                                    onChange={(v) => setLazyOneMin(v === "" ? null : Number(v))}
                                                    disabled={(showHourPicker && lazyHour == null) || (showQuarterPicker && lazyQuarter == null) || (showFiveMinPicker && lazyFiveMin == null)}
                                                    data={Array.from({ length: oneMinsInActiveFiveMin }, (_, k) => {
                                                      const baseHourSec = showHourPicker ? (lazyHour ?? 0) * 3600 : 0;
                                                      const baseQuarterSec = showQuarterPicker ? (lazyQuarter ?? 0) * 900 : 0;
                                                      const baseFiveSec = showFiveMinPicker ? (lazyFiveMin ?? 0) * 300 : 0;
                                                      const offsetSec = baseHourSec + baseQuarterSec + baseFiveSec + k * 60;
                                                      const fmt = (s: number): string => Number.isFinite(selectedWindowStartMs)
                                                        ? new Date(selectedWindowStartMs + s * 1000)
                                                            .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                                        : `+${s}s`;
                                                      return { value: String(k), label: `${fmt(offsetSec)}\u2013${fmt(offsetSec + 60)}` };
                                                    })}
                                                  />
                                                  {lazyOneMin != null ? (
                                                    <Button
                                                      size="compact-xs"
                                                      variant="subtle"
                                                      onClick={() => setLazyOneMin(null)}
                                                    >
                                                      clear
                                                    </Button>
                                                  ) : null}
                                                </Group>
                                              ) : null}
                                            </Stack>
                                        </Stack>
                                        {selectedBucketIndex == null ? (
                                          <Text c="dimmed" size="sm">
                                            {lazyLoadDetail
                                              ? "Drill down to a 1-min slice above to load its 1-second price feed."
                                              : oneMinRequired
                                              ? "Pick a 1-min bucket above to load its 1-second price feed."
                                              : "Pick a 5-min bucket (and an optional 1-min refinement) to load its 1-second price feed."}
                                          </Text>
                                        ) : selectedTicksQ.isLoading ? (
                                          <LoadingState label="Loading 1s price feed..." />
                                        ) : selectedTicks.length === 0 ? (
                                          <Text c="dimmed" size="sm">No tick data for this window.</Text>
                                        ) : (
                                          <ScrollArea h={420}>
                                            <Table withTableBorder horizontalSpacing="sm" verticalSpacing={5} style={monoStyle} fz="sm">
                                              <Table.Thead>
                                                <Table.Tr>
                                                  <Table.Th>Time</Table.Th>
                                                  <Table.Th>Secs</Table.Th>
                                                  <Table.Th>BTC Price</Table.Th>
                                                  <Table.Th>UP %</Table.Th>
                                                  <Table.Th>DOWN %</Table.Th>
                                                </Table.Tr>
                                              </Table.Thead>
                                              <Table.Tbody>
                                                {Array.from({ length: viewBucketSeconds }, (_, secondInMinute) => {
                                                  const n = selectedBucketIndex;
                                                  const tick = simPriceFeedBySecond.get(n)?.get(secondInMinute) ?? null;
                                                  const offsetSec = (n - 1) * viewBucketSeconds + secondInMinute;
                                                  const rowTime = Number.isFinite(selectedWindowStartMs)
                                                    ? new Date(selectedWindowStartMs + offsetSec * 1000).toLocaleTimeString()
                                                    : "—";
                                                  const secsLeftLabel = `${marketWindowSeconds - offsetSec}s`;
                                                  return (
                                                    <Table.Tr key={`${n}-${secondInMinute}`} style={tick ? undefined : { opacity: 0.45 }}>
                                                      <Table.Td>{rowTime}</Table.Td>
                                                      <Table.Td>{tick ? `${tick.secs_left}s` : secsLeftLabel}</Table.Td>
                                                      <Table.Td>{tick ? fmtUsd(tick.btc_price) : "—"}</Table.Td>
                                                      <Table.Td>{tick ? fmtPct1(directionalMidOdds(tick, "UP")) : "—"}</Table.Td>
                                                      <Table.Td>{tick ? fmtPct1(directionalMidOdds(tick, "DOWN")) : "—"}</Table.Td>
                                                    </Table.Tr>
                                                  );
                                                })}
                                              </Table.Tbody>
                                            </Table>
                                          </ScrollArea>
                                        )}
                                      </Stack>
                                    </Card>

                                    <Card withBorder>
                                      <Stack gap="xs">
                                        <Group justify="space-between" align="center">
                                          <Title order={5}>Condition check (simulated per tick)</Title>
                                          {selectedOutcomeTraceQ.isSuccess && selectedBucketIndex != null && bucketScopedChecks.length > 0 ? (
                                            <Badge variant="light">
                                              {bucketScopedChecks.length} of {visibleChecks.length} checks
                                            </Badge>
                                          ) : null}
                                        </Group>
                                        {/*
                                          V3 multi-market: scoped to the bucket picked above. 4-hour
                                          windows can emit 10,000+ per-second condition checks; rendering
                                          them all blocked the main thread for several seconds. Now we
                                          only mount the checks falling inside the active bucket's slice.
                                        */}
                                        {selectedBucketIndex == null ? (
                                          <Text c="dimmed" size="sm">
                                            {lazyLoadDetail
                                              ? "Drill down to a 1-min slice above to load its condition checks."
                                              : oneMinRequired
                                              ? "Pick a 1-min bucket above to load its condition checks."
                                              : "Pick a 5-min bucket (and an optional 1-min refinement) to load its condition checks."}
                                          </Text>
                                        ) : selectedOutcomeTraceQ.isError ? (
                                          <ErrorState error={selectedOutcomeTraceQ.error} onRetry={() => selectedOutcomeTraceQ.refetch()} />
                                        ) : selectedOutcomeTraceQ.isLoading || selectedTicksQ.isLoading ? (
                                          <LoadingState label="Loading condition check trace..." />
                                        ) : visibleChecks.length === 0 ? (
                                          <Text c="dimmed" size="sm">No simulated checks for this window.</Text>
                                        ) : bucketScopedChecks.length === 0 ? (
                                          <Text c="dimmed" size="sm">
                                            No condition checks landed inside this bucket.
                                          </Text>
                                        ) : (
                                          <ScrollArea h={810}>
                                            <Table
                                              withTableBorder
                                              horizontalSpacing="sm"
                                              verticalSpacing={7}
                                              style={monoStyle}
                                              fz="md"
                                              stickyHeader
                                              stickyHeaderOffset={0}
                                            >
                                              <Table.Thead>
                                                <Table.Tr>
                                                  <Table.Th>Time</Table.Th>
                                                  <Table.Th>Check Status</Table.Th>
                                                  <Table.Th>Dir</Table.Th>
                                                  <Table.Th>Min</Table.Th>
                                                  <Table.Th>Current</Table.Th>
                                                  <Table.Th>Max</Table.Th>
                                                  <Table.Th>Candles</Table.Th>
                                                  <Table.Th>Momentum</Table.Th>
                                                  <Table.Th>Avg Momentum</Table.Th>
                                                </Table.Tr>
                                              </Table.Thead>
                                              <Table.Tbody>
                                                {bucketScopedChecks.map(({ row, fullIdx }) => {
                                                  // ``priorRows`` walks the full visibleChecks list up to this row
                                                  // so odds-history chips reflect everything observed before the
                                                  // tick, even when the user is only displaying one bucket.
                                                  const idx = fullIdx;
                                                  const priorRows = visibleChecks.slice(0, fullIdx);
                                                  const odds = checkedOddsSummary(row, priorRows);
                                                  const chips = oddsChips(odds.values);
                                                  const status = simulatedCheckStatus(row, placeCheck?.check_ts ?? null, stopTakeExitTs, selectedOutcome.result);
                                                  const fav = row.favorite_odds == null ? null : Number(row.favorite_odds);
                                                  const favFinite = fav != null && Number.isFinite(fav);
                                                  // Prefer thresholds stored in the trace over the current form config.
                                                  // The simulation may have run with different min/max odds or a
                                                  // late-buy bypass — the trace's own checks reflect what the engine
                                                  // actually evaluated, so the ORDER PLACED row is never misleading.
                                                  const bandCheck = row.checks.find((c) => c.name === "odds_in_band");
                                                  const bandThreshold = bandCheck?.threshold as { min?: unknown; max?: unknown } | null | undefined;
                                                  const tracedMin = bandThreshold ? toFiniteNumber((bandThreshold as Record<string, unknown>).min, NaN) : NaN;
                                                  const tracedMax = bandThreshold ? toFiniteNumber((bandThreshold as Record<string, unknown>).max, NaN) : NaN;
                                                  const effectiveMinOdds = Number.isFinite(tracedMin) ? tracedMin : toFiniteNumber(configOverride?.min_odds, Number.NaN);
                                                  const effectiveMaxOdds = Number.isFinite(tracedMax) ? tracedMax : toFiniteNumber(configOverride?.max_odds, Number.NaN);
                                                  const minOdds = effectiveMinOdds;
                                                  const maxOdds = effectiveMaxOdds;
                                                  const minMatchingCandles = toFiniteNumber(configOverride?.min_matching_candles, Number.NaN);
                                                  const minPassed = bandCheck != null
                                                    ? (bandCheck.passed ? (favFinite && Number.isFinite(effectiveMinOdds) ? fav! >= effectiveMinOdds : true) : false)
                                                    : (favFinite && Number.isFinite(minOdds) ? fav! >= minOdds : null);
                                                  const maxPassed = bandCheck != null
                                                    ? (bandCheck.passed ? (favFinite && Number.isFinite(effectiveMaxOdds) ? fav! <= effectiveMaxOdds : true) : false)
                                                    : (favFinite && Number.isFinite(maxOdds) ? fav! <= maxOdds : null);
                                                  const candleCheck = row.checks.find((c) => c.name === "candle_agreement");
                                                  const candlesPassed = candleCheck != null
                                                    ? (candleCheck.passed == null ? null : Boolean(candleCheck.passed))
                                                    : (row.matching_candles == null || !Number.isFinite(minMatchingCandles)
                                                      ? null
                                                      : row.matching_candles >= minMatchingCandles);
                                                  return (
                                                    <Fragment key={`${row.check_ts}-${idx}`}>
                                                      <Table.Tr>
                                                        <Table.Td>{new Date(row.check_ts).toLocaleTimeString()}</Table.Td>
                                                        <Table.Td>
                                                          <Group gap={6} wrap="wrap">
                                                            <Badge
                                                              size="md"
                                                              variant="filled"
                                                              styles={{ root: pillStylesForTone(status.tone) }}
                                                            >
                                                              {status.label}
                                                            </Badge>
                                                            {row.outcome === "stop_loss" ? (
                                                              <Badge size="md" variant="light" color="green">SL ✓</Badge>
                                                            ) : null}
                                                          </Group>
                                                        </Table.Td>
                                                        <Table.Td>{row.direction ?? "—"}</Table.Td>
                                                        <Table.Td>
                                                          <Group gap={6} wrap="nowrap">
                                                            <Text style={monoStyle}>{Number.isFinite(minOdds) ? formatPctNum(minOdds) : "—"}</Text>
                                                            {passFailIcon(minPassed)}
                                                          </Group>
                                                        </Table.Td>
                                                        <Table.Td style={monoStyle}>{favFinite ? formatPctNum(fav) : "—"}</Table.Td>
                                                        <Table.Td>
                                                          <Group gap={6} wrap="nowrap">
                                                            <Text style={monoStyle}>{Number.isFinite(maxOdds) ? formatPctNum(maxOdds) : "—"}</Text>
                                                            {passFailIcon(maxPassed)}
                                                          </Group>
                                                        </Table.Td>
                                                        <Table.Td>
                                                          <Group gap={6} wrap="nowrap">
                                                            <Text style={monoStyle}>{candlesLabel(row, priorRows)}</Text>
                                                            {passFailIcon(candlesPassed)}
                                                          </Group>
                                                        </Table.Td>
                                                        <Table.Td>
                                                          <Group gap={6} wrap="nowrap">
                                                            <Text style={monoStyle}>{momentumLabel(row, priorRows)}</Text>
                                                            {passFailIcon(momentumPassed(row))}
                                                          </Group>
                                                        </Table.Td>
                                                        <Table.Td>{avgMomentumCell(row)}</Table.Td>
                                                      </Table.Tr>
                                                      <Table.Tr>
                                                        <Table.Td colSpan={9}>
                                                          <Group gap={6} wrap="wrap">
                                                            <Text size="md" c="dimmed">Odds checked:</Text>
                                                            {chips.length === 0 ? (
                                                              <Badge
                                                                size="lg"
                                                                variant="filled"
                                                                styles={{ root: pillStylesForTone(odds.passed === false ? "red" : odds.passed === true ? "green" : "gray") }}
                                                              >
                                                                —
                                                              </Badge>
                                                            ) : chips.map((o, i) => (
                                                              <Badge
                                                                key={`${row.check_ts}-${idx}-odd-${i}`}
                                                                size="lg"
                                                                variant="filled"
                                                                styles={{ root: pillStylesForTone(o.passed === false ? "red" : o.passed === true ? "green" : "gray") }}
                                                              >
                                                                {o.value}
                                                              </Badge>
                                                            ))}
                                                          </Group>
                                                        </Table.Td>
                                                      </Table.Tr>
                                                    </Fragment>
                                                  );
                                                })}
                                              </Table.Tbody>
                                            </Table>
                                          </ScrollArea>
                                        )}
                                      </Stack>
                                    </Card>

                                    {msTrackingRows.length > 0 ? (
                                      <Card withBorder>
                                        <Stack gap="xs">
                                          <Group justify="space-between" align="center">
                                            <Stack gap={2}>
                                              <Title order={5}>Momentum Sell Tracking (post-fill)</Title>
                                              <Text size="xs" c="dimmed">
                                                Rolling window of post-fill same-side ASK prices (rounded up). Shows every second after fill until exit or window close.
                                              </Text>
                                            </Stack>
                                            <Badge
                                              color={selectedOutcome.result === "momentum_sell" ? "orange" : "gray"}
                                              variant="light"
                                              size="lg"
                                            >
                                              {selectedOutcome.result === "momentum_sell" ? "TRIGGERED" : "NOT TRIGGERED"}
                                            </Badge>
                                          </Group>
                                          <ScrollArea h={520} type="auto">
                                            <Table
                                              withTableBorder
                                              horizontalSpacing="sm"
                                              verticalSpacing={5}
                                              style={monoStyle}
                                              fz="sm"
                                              stickyHeader
                                              stickyHeaderOffset={0}
                                            >
                                              <Table.Thead>
                                                <Table.Tr>
                                                  <Table.Th>Time</Table.Th>
                                                  <Table.Th>Secs Left</Table.Th>
                                                  <Table.Th>Side Ask (↑¢)</Table.Th>
                                                  <Table.Th>Avg</Table.Th>
                                                  <Table.Th>Fill Ask</Table.Th>
                                                  <Table.Th>Buffer</Table.Th>
                                                  <Table.Th>SL Level</Table.Th>
                                                  <Table.Th>SL Secs ↓</Table.Th>
                                                  <Table.Th>TP Level</Table.Th>
                                                  <Table.Th>TP Active</Table.Th>
                                                  <Table.Th>TP Secs ↓</Table.Th>
                                                  <Table.Th>Status</Table.Th>
                                                </Table.Tr>
                                              </Table.Thead>
                                              <Table.Tbody>
                                                {msTrackingRows.map((row, idx) => {
                                                  const msCheck = row.checks.find((c) => c.name === "momentum_sell");
                                                  if (!msCheck) return null;
                                                  const v = (msCheck.value && typeof msCheck.value === "object" && !Array.isArray(msCheck.value))
                                                    ? (msCheck.value as Record<string, unknown>)
                                                    : {};
                                                  const toN = (x: unknown): number | null => {
                                                    const n = Number(x);
                                                    return Number.isFinite(n) ? n : null;
                                                  };
                                                  const avg = toN(v.avg);
                                                  const fillAsk = toN(v.fill_ask);
                                                  const slTrigger = toN(v.sl_trigger);
                                                  const tpTrigger = toN(v.tp_trigger);
                                                  const slBelowCount = toN(v.sl_below_count);
                                                  const tpBelowCount = toN(v.tp_below_count);
                                                  const tpActivated = v.tp_activated === true;
                                                  const collected = toN(v.collected);
                                                  const windowSecs = toN(v.window_seconds);
                                                  const sideAsk = msTickAskMap.get(row.check_ts) ?? null;
                                                  const isFinal = row.outcome === "momentum_sell";
                                                  const isWarmup = collected != null && windowSecs != null && collected < windowSecs;
                                                  let avgColor: string | undefined;
                                                  if (avg != null && fillAsk != null) {
                                                    if (slTrigger != null && avg < slTrigger) {
                                                      avgColor = "var(--mantine-color-red-5)";
                                                    } else if (avg < fillAsk) {
                                                      avgColor = "var(--mantine-color-orange-5)";
                                                    } else if (tpTrigger != null && avg >= tpTrigger) {
                                                      avgColor = "var(--mantine-color-teal-5)";
                                                    } else {
                                                      avgColor = "var(--mantine-color-green-6)";
                                                    }
                                                  }
                                                  return (
                                                    <Table.Tr
                                                      key={`ms-${row.check_ts}-${idx}`}
                                                      style={isFinal ? { background: "rgba(255,140,0,0.13)", fontWeight: 700 } : isWarmup ? { opacity: 0.6 } : undefined}
                                                    >
                                                      <Table.Td>{new Date(row.check_ts).toLocaleTimeString()}</Table.Td>
                                                      <Table.Td>{row.secs_left}</Table.Td>
                                                      <Table.Td>{sideAsk != null ? sideAsk.toFixed(2) : "—"}</Table.Td>
                                                      <Table.Td style={{ color: avgColor, fontWeight: avg != null ? 600 : undefined }}>
                                                        {avg != null ? avg.toFixed(4) : isWarmup ? <Text size="xs" c="dimmed">warming…</Text> : "—"}
                                                      </Table.Td>
                                                      <Table.Td>{fillAsk != null ? fillAsk.toFixed(4) : "—"}</Table.Td>
                                                      <Table.Td>
                                                        {collected != null && windowSecs != null
                                                          ? <Text size="xs" c={isWarmup ? "dimmed" : undefined}>{collected}/{windowSecs}</Text>
                                                          : "—"}
                                                      </Table.Td>
                                                      <Table.Td>{slTrigger != null ? slTrigger.toFixed(4) : "—"}</Table.Td>
                                                      <Table.Td>
                                                        {slBelowCount != null && slTrigger != null
                                                          ? <Text c={slBelowCount > 0 ? "red" : undefined}>{slBelowCount}</Text>
                                                          : "—"}
                                                      </Table.Td>
                                                      <Table.Td>{tpTrigger != null ? tpTrigger.toFixed(4) : "—"}</Table.Td>
                                                      <Table.Td>
                                                        {tpTrigger != null
                                                          ? (tpActivated
                                                            ? <Badge size="xs" color="teal">YES</Badge>
                                                            : <Badge size="xs" color="gray">NO</Badge>)
                                                          : "—"}
                                                      </Table.Td>
                                                      <Table.Td>
                                                        {tpBelowCount != null && tpTrigger != null && tpActivated
                                                          ? <Text c={tpBelowCount > 0 ? "yellow" : undefined}>{tpBelowCount}</Text>
                                                          : "—"}
                                                      </Table.Td>
                                                      <Table.Td>
                                                        {isFinal
                                                          ? <Badge size="sm" color="orange">SOLD</Badge>
                                                          : <Text size="xs" c="dimmed" style={{ maxWidth: 240 }}>{msCheck.detail ?? "—"}</Text>}
                                                      </Table.Td>
                                                    </Table.Tr>
                                                  );
                                                })}
                                              </Table.Tbody>
                                            </Table>
                                          </ScrollArea>
                                        </Stack>
                                      </Card>
                                    ) : null}
                                  </>
                                ) : null}
                              </Stack>
                            </>
                          ) : null}
                        </>
                      ) : null}
                    </>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {activeCfgQ.isLoading || versionsQ.isLoading ? <LoadingState label="Loading configs..." /> : null}
      {activeCfgQ.isError ? <ErrorState error={activeCfgQ.error as Error} onRetry={() => activeCfgQ.refetch()} /> : null}
      {versionsQ.isError ? <ErrorState error={versionsQ.error as Error} onRetry={() => versionsQ.refetch()} /> : null}

    </Stack>
  );
}

function Stat({
  label,
  value,
  accent,
  subValue,
  subAccent,
}: {
  label: string;
  value: string;
  accent?: "green" | "red";
  subValue?: string;
  subAccent?: "green" | "red";
}) {
  const color = accent === "green"
    ? "var(--mantine-color-green-6)"
    : accent === "red"
      ? "var(--mantine-color-red-6)"
      : undefined;
  const subColor = subAccent === "green"
    ? "var(--mantine-color-green-6)"
    : subAccent === "red"
      ? "var(--mantine-color-red-6)"
      : "var(--mantine-color-dimmed)";
  return (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{label}</Text>
      <Text fw={800} style={monoStyle} c={color}>{value}</Text>
      {subValue ? <Text size="xs" c={subColor} style={monoStyle}>{subValue}</Text> : null}
    </Paper>
  );
}

function toSummaryView(raw: unknown): SimSummaryView | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const subtractNoFillsFromResults = Boolean(r.subtract_no_fills_from_results ?? false);
  const outcomesRaw = Array.isArray(r.outcomes) ? r.outcomes : [];
  const outcomes: SimOutcomeRow[] = outcomesRaw
    .filter((x): x is Record<string, unknown> => Boolean(x && typeof x === "object"))
    .map((x) => ({
      slug: String(x.slug ?? ""),
      result: String(x.result ?? "no_bet") as SimOutcomeRow["result"],
      historical_no_fill: Boolean(x.historical_no_fill ?? false),
      direction: asDirection(x.direction),
      winning_direction: asDirection(x.winning_direction),
      favorite_odds: x.favorite_odds == null ? null : String(x.favorite_odds),
      pnl_usdc: Number(x.pnl_usdc ?? 0),
      trade_size_usdc: x.trade_size_usdc != null ? Number(x.trade_size_usdc) : null,
      win_streak_at_bet: Number(x.win_streak_at_bet ?? 0),
      decision_outcome: String(x.decision_outcome ?? ""),
      counterpart_override_active: Boolean(x.counterpart_override_active ?? false),
      counterpart_match_order_active: Boolean(x.counterpart_match_order_active ?? false),
      check_trace: Array.isArray(x.check_trace)
        ? x.check_trace
          .filter((r): r is Record<string, unknown> => Boolean(r && typeof r === "object"))
          .map((r) => ({
            check_ts: String(r.check_ts ?? ""),
            secs_left: Number(r.secs_left ?? 0),
            outcome: String(r.outcome ?? "monitor") as SimTraceRow["outcome"],
            direction: asDirection(r.direction),
            favorite_odds: r.favorite_odds == null ? null : String(r.favorite_odds),
            matching_candles: r.matching_candles == null ? null : Number(r.matching_candles),
            candles_evaluated: r.candles_evaluated == null ? null : Number(r.candles_evaluated),
            block_reason: r.block_reason == null ? null : String(r.block_reason),
            checks: Array.isArray(r.checks)
              ? r.checks
                .filter((c): c is Record<string, unknown> => Boolean(c && typeof c === "object"))
                .map((c) => ({
                  name: String(c.name ?? "unknown"),
                  passed: typeof c.passed === "boolean" ? c.passed : c.passed == null ? null : Boolean(c.passed),
                  value: c.value,
                  threshold: c.threshold,
                  detail: c.detail == null ? null : String(c.detail),
                }))
              : [],
          }))
          .filter((r) => r.check_ts.length > 0)
        : [],
    }))
    .filter((x) => x.slug.length > 0);

  const countedOutcomes = subtractNoFillsFromResults
    ? outcomes.filter((o) => !o.historical_no_fill)
    : outcomes;

  const totalWon = countedOutcomes.reduce((sum, o) => (o.pnl_usdc > 0 ? sum + o.pnl_usdc : sum), 0);
  const totalLost = countedOutcomes.reduce((sum, o) => (o.pnl_usdc < 0 ? sum + Math.abs(o.pnl_usdc) : sum), 0);
  const fallbackTakeProfitCount = countedOutcomes.reduce((sum, o) => (o.result === "take_profit" ? sum + 1 : sum), 0);
  const fallbackTakeProfitWon = countedOutcomes.reduce((sum, o) => (o.result === "take_profit" && o.pnl_usdc > 0 ? sum + o.pnl_usdc : sum), 0);
  const fallbackStopLossCount = countedOutcomes.reduce((sum, o) => (o.result === "stop_loss" ? sum + 1 : sum), 0);
  const fallbackStopLossLost = countedOutcomes.reduce((sum, o) => (o.result === "stop_loss" && o.pnl_usdc < 0 ? sum + Math.abs(o.pnl_usdc) : sum), 0);
  const fallbackMomentumSellCount = countedOutcomes.reduce((sum, o) => (o.result === "momentum_sell" ? sum + 1 : sum), 0);
  const fallbackMomentumSellPnl = countedOutcomes.reduce((sum, o) => (o.result === "momentum_sell" ? sum + o.pnl_usdc : sum), 0);

  const rangeBreakdownRaw = Array.isArray(r.time_range_breakdown) ? r.time_range_breakdown : [];
  const timeRangeBreakdown: SimRangeBreakdownRow[] = rangeBreakdownRaw
    .filter((x): x is Record<string, unknown> => Boolean(x && typeof x === "object"))
    .map((x) => ({
      range: String(x.range ?? "—"),
      total_windows: Number(x.total_windows ?? 0),
      windows_with_bet: Number(x.windows_with_bet ?? 0),
      wins: Number(x.wins ?? 0),
      losses: Number(x.losses ?? 0),
      win_rate: String(x.win_rate ?? "0"),
      total_won_usdc: String(x.total_won_usdc ?? "0"),
      total_lost_usdc: String(x.total_lost_usdc ?? "0"),
      net_pnl_usdc: String(x.net_pnl_usdc ?? "0"),
    }));

  const dayBreakdownRaw = Array.isArray(r.day_breakdown) ? r.day_breakdown : [];
  const dayBreakdown: SimDayBreakdownRow[] = dayBreakdownRaw
    .filter((x): x is Record<string, unknown> => Boolean(x && typeof x === "object"))
    .map((x) => ({
      day: String(x.day ?? "—"),
      total_windows: Number(x.total_windows ?? 0),
      windows_with_bet: Number(x.windows_with_bet ?? 0),
      wins: Number(x.wins ?? 0),
      losses: Number(x.losses ?? 0),
      win_rate: String(x.win_rate ?? "0"),
      total_won_usdc: String(x.total_won_usdc ?? "0"),
      total_lost_usdc: String(x.total_lost_usdc ?? "0"),
      net_pnl_usdc: String(x.net_pnl_usdc ?? "0"),
    }));

  return {
    total_windows: Number(r.total_windows ?? 0),
    windows_with_bet: Number(r.windows_with_bet ?? 0),
    wins: Number(r.wins ?? 0),
    losses: Number(r.losses ?? 0),
    take_profits: Number(r.take_profits ?? fallbackTakeProfitCount),
    stop_losses: Number(r.stop_losses ?? fallbackStopLossCount),
    momentum_sells: Number(r.momentum_sells ?? fallbackMomentumSellCount),
    momentum_sell_profit_count: Number(r.momentum_sell_profit_count ?? 0),
    momentum_sell_loss_count: Number(r.momentum_sell_loss_count ?? 0),
    momentum_sell_profit_pnl_usdc: String(r.momentum_sell_profit_pnl_usdc ?? "0"),
    momentum_sell_loss_usdc: String(r.momentum_sell_loss_usdc ?? "0"),
    momentum_sell_total_pnl_usdc: String(r.momentum_sell_total_pnl_usdc ?? fallbackMomentumSellPnl.toFixed(4)),
    losses_including_stop_losses: Number(
      r.losses_including_stop_losses
        ?? (Number(r.losses ?? 0) + Number(r.stop_losses ?? fallbackStopLossCount)),
    ),
    no_fill_wins: Number(r.no_fill_wins ?? 0),
    no_fill_losses: Number(r.no_fill_losses ?? 0),
    skips: Number(r.skips ?? 0),
    blocked: Number(r.blocked ?? 0),
    no_bet: Number(r.no_bet ?? 0),
    unresolved: Number(r.unresolved ?? 0),
    pnl_usdc: String(r.pnl_usdc ?? "0"),
    total_won_usdc: String(r.total_won_usdc ?? totalWon.toFixed(4)),
    take_profit_total_won_usdc: String(r.take_profit_total_won_usdc ?? fallbackTakeProfitWon.toFixed(4)),
    total_lost_usdc: String(r.total_lost_usdc ?? totalLost.toFixed(4)),
    stop_loss_total_lost_usdc: String(r.stop_loss_total_lost_usdc ?? fallbackStopLossLost.toFixed(4)),
    fees_usdc: String(r.fees_usdc ?? "0"),
    win_rate: String(r.win_rate ?? "0"),
    average_odds: r.average_odds == null ? null : String(r.average_odds),
    time_range_breakdown: timeRangeBreakdown,
    day_breakdown: dayBreakdown,
    gate_impact: (() => {
      const parsed = SimulationGateImpact.safeParse(r.gate_impact);
      return parsed.success ? parsed.data : null;
    })(),
    outcomes,
  };
}

function valueToInput(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

function inputToValue(raw: string, template: unknown): unknown {
  if (template == null) return raw === "" ? null : raw;
  if (typeof template === "boolean") return raw.toLowerCase() === "true";
  if (typeof template === "number") {
    const n = Number(raw.trim().replace(/%$/, ""));
    return Number.isFinite(n) ? n : raw;
  }
  if (typeof template === "string") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}
